import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

import { ApiService, ApoloContactSearchPayload } from '../api/api.service';
import { environment } from '../../environments/environment';
import { StorageService } from './storage.service';
import {
  AgricolaAddOn,
  AgricolaApiResponse,
  AgricolaAssistanceFirebaseStatus,
  AgricolaAssistancePrivateLinkCreatePayload,
  AgricolaAssistancePrivateLinkNotificationPayload,
  AgricolaAssistancePrivateLinkState,
  AgricolaAssistanceSignatureFlow,
  AgricolaAssistancePlansPayload,
  AgricolaAssistanceValidation,
  AgricolaBalanceValidatePayload,
  AgricolaBeneficiary,
  AgricolaCatalogOption,
  AgricolaClientAccountsPayload,
  AgricolaClientInfo,
  AgricolaClientInfoPayload,
  AgricolaClientSearchCriteria,
  AgricolaCountry,
  AgricolaCoverage,
  AgricolaCreatePolicyPayload,
  AgricolaCoverageOption,
  AgricolaInsuranceDestination,
  AgricolaDashboardCard,
  AgricolaInsuranceHolder,
  AgricolaInsurancePayment,
  AgricolaPlan,
  AgricolaPlanDetail,
  AgricolaPlanPriceRange,
  AgricolaProduct,
  AgricolaPreloadedPaymentInfo,
  AgricolaSellerValidatePayload,
  AssistanceDataToSend,
  InsuranceBeneficiary,
  InsuranceDependent,
  InsuranceSalePayload,
  RelationshipCatalogStrategy,
  RelationshipOption,
  PolicyDocuments,
  ReportQueueItem,
  ReportQueueResponse,
  SellReportFilter,
  SellReportResponse,
  SignEmissionLog
} from '../interfaces/agricola.interface';
import { AgricolaFirebaseSyncService } from './agricola-firebase-sync.service';
import { AgricolaPaymentCryptoService } from './agricola-payment-crypto.service';
import { MeService } from './me.service';

// Maps UI search-type values to the single-char idbusqueda codes expected by S2.
// Confirm codes against S2 API documentation when available.
const SEARCH_TYPE_CODES: Record<string, string> = {
  'numero-unico':    '1',
  'dui':             '2',
  'nit':             '3',
  'carnet-residente':'4',
  'pasaporte':       '5',
};

const SIS_AGRICOLA_DEFAULTS = {
  agencyCode: '250',
  userCode: 'SISCHUBB',
  allyCode: '002',
  insuranceOption: '01',
  defaultProductCode: '501',
  defaultInsuredAmount: '3000000'
};

const ASSISTANCE_COMPANY = 'BAES';

// Código de "tarjeta de crédito" en la escala que SIS usa para cobrar
// (tipoCuenta de /balance/validate y tipoCobro de /policies): 1 = cuenta
// corriente o ahorro, 2 = TDC. No confundir con CodTipoCta de /clients/accounts
// (1=corriente, 2=ahorro, 3=TDC), que es la escala de la consulta de cuentas.
const AGRICOLA_TDC_CHARGE_CODE = '2';

// Relleno con el que se reconstruye un número de tarjeta de 16 dígitos a partir
// de los últimos 4, único dato del PAN que el banco devuelve sin enmascarar. Es
// el mismo prefijo que ya usa telemarketing (agricola-fre en s2plus-cuentame),
// y el core lo necesita con esta forma: get_credit_card_emissor deriva ccCd y
// branchCd del primer dígito (4 = VISA) y la referencia last_four_digits toma
// payment_number[-4:]. Mandar el número de cuenta acá registraba en Chubb un
// emisor arbitrario y unos últimos 4 que no son los de la tarjeta.
const AGRICOLA_CARD_EMISSION_PREFIX = '411100000000';

// Último recurso para phoneNumber1 del payload de asistencias, el único campo
// de teléfono que no puede ir vacío. Sólo se usa cuando el titular no tiene
// ningún número real: el relleno de S2 ('00000000000') ya se descartó antes.
const AGRICOLA_EMPTY_PHONE = '00000000';

const SIS_PRODUCT_CODES: Record<string, string> = {
  Vida: '501',
  op1: '501'
};

// GURU = GURO (assistance ally, the only one enabled today); ACSA is the
// insurance ally, wired here so it activates without a follow-up code change.
const SIS_ALLY_CODES: Record<string, string> = {
  GURU: '003',
  ACSA: '002'
};

// Plan/product config key that opts an assistance into dependants
// (`{ "dependents": true }`). Without it the product is holder-only.
const DEPENDENTS_CONFIG_KEY = 'dependents';

const PRODUCT_CODE_BY_SELECTION: Record<string, string> = {
  Vida: 'AGRICOLA_FRE_CYBER',
  'Producto 2': 'AGRICOLA_FRE_CYBER_MUVIT',
  AGRICOLA_FRE_CYBER: 'AGRICOLA_FRE_CYBER',
  AGRICOLA_FRE_CYBER_MUVIT: 'AGRICOLA_FRE_CYBER_MUVIT'
};

export type AgricolaLoadingKey =
  | 'clientSearch'
  | 'holder'
  | 'products'
  | 'plans'
  | 'countries'
  | 'states'
  | 'substates'
  | 'cities'
  | 'identificationTypes'
  | 'genders'
  | 'creationAddresses'
  | 'relationships'
  | 'clientAccounts';

export type AgricolaLoadingState = Record<AgricolaLoadingKey, boolean>;

const INITIAL_LOADING_STATE: AgricolaLoadingState = {
  clientSearch: false,
  holder: false,
  products: false,
  plans: false,
  countries: false,
  states: false,
  substates: false,
  cities: false,
  identificationTypes: false,
  genders: false,
  creationAddresses: false,
  relationships: false,
  clientAccounts: false
};


@Injectable({
  providedIn: 'root'
})
export class AgricolaService {
  private _currentClient: AgricolaClientInfo | null = null;
  private currentProducts: AgricolaProduct[] = [];
  private currentCountries: AgricolaCountry[] = [];
  private currentStates: AgricolaCatalogOption[] = [];
  private currentSubstates: AgricolaCatalogOption[] = [];
  private currentCities: AgricolaCatalogOption[] = [];
  private currentGenders: AgricolaCatalogOption[] = [];
  private currentPlans: AgricolaPlan[] = [];
  private _isAssistanceProduct = false;
  private _availableAddOns: AgricolaAddOn[] = [];
  private _selectedAddOns: AgricolaAddOn[] = [];
  private _lastClientSearch: AgricolaClientSearchCriteria | null = null;
  private _preloadedPaymentInfo: AgricolaPreloadedPaymentInfo | null = null;
  private _emissionCorrelationId: string | null = null;
  // Modo "Aperturas Telemarketing": la captura es idéntica al F2F, pero al final
  // NO emite — deja la venta PENDIENTE en core y muestra el process_number.
  private _tmkMode = false;
  private readonly loadingStateSubject = new BehaviorSubject<AgricolaLoadingState>({ ...INITIAL_LOADING_STATE });
  currentInsuranceInfo: Partial<InsuranceSalePayload> = {};

  readonly loadingState$ = this.loadingStateSubject.asObservable();

  get currentClient(): AgricolaClientInfo | null {
    return this._currentClient;
  }

  get preloadedPaymentInfo(): AgricolaPreloadedPaymentInfo | null {
    return this._preloadedPaymentInfo ? { ...this._preloadedPaymentInfo } : null;
  }

  get loadingSnapshot(): AgricolaLoadingState {
    return { ...this.loadingStateSubject.value };
  }

  get isAssistanceProduct(): boolean {
    return this._isAssistanceProduct;
  }

  get tmkMode(): boolean {
    return this._tmkMode;
  }

  set tmkMode(value: boolean) {
    this._tmkMode = value;
  }

  get availableAddOns(): AgricolaAddOn[] {
    return this._availableAddOns.map(addon => ({ ...addon }));
  }

  get selectedAddOns(): AgricolaAddOn[] {
    return this._selectedAddOns.map(addon => ({ ...addon }));
  }

  constructor(
    private readonly api: ApiService,
    private readonly meService: MeService,
    private readonly paymentCrypto?: AgricolaPaymentCryptoService,
    private readonly storage?: StorageService,
    private readonly firebaseSync?: AgricolaFirebaseSyncService
  ) {}

  initGenericInsurance(): void {
    const today = this.todayIsoDate();

    this._currentClient = null;
    this._preloadedPaymentInfo = null;
    this._emissionCorrelationId = this.generateCorrelationId();
    this.clearAssistanceState();
    this.currentInsuranceInfo = {
      date_issued: today,
      date_effective: today,
      self_insurance: true,
      policy_number: '',
      payment_info: {
        // Semilla: el valor real lo fija buildPaymentInfo según el tipo de
        // cuenta (0 en TDC, 1 en débito). Se siembra en 0 porque es lo que ese
        // mismo cálculo da mientras no hay tipo de cuenta: sin dato,
        // balanceValidationTypeCode cae en TDC y isCreditCardAccount es true.
        // Además es la dirección segura: no cobrar de más contra un bill 2050.
        policy_charge: 0,
        frequency: 'E'
      }
    };
  }

  setCurrentClientSearch(criteria: AgricolaClientSearchCriteria): void {
    this.ensureCurrentInsuranceInfo();

    // Only a document-based search seeds the holder's identification: the
    // "número único" is not an identity document, and seeding it here also left
    // 'numero-unico' as the identification_type when the client response
    // carried no tipoDocuIdentidad. When no document comes back at all,
    // buildHolderFromClientInfo still falls back to the número único.
    const isDocumentSearch = this.toStringValue(criteria.searchType) !== 'numero-unico';
    const identificationId = isDocumentSearch ? this.toStringValue(criteria.searchValue).trim() : '';
    const identificationType = isDocumentSearch ? this.toStringValue(criteria.searchType) : '';
    const searchHolder = {
      ...(this.currentInsuranceInfo.holder || {}),
      identification_id: identificationId,
      identification_type: identificationType,
      name: this.currentInsuranceInfo.holder?.name || ''
    };

    this.currentInsuranceInfo = {
      ...this.currentInsuranceInfo,
      search_criteria: { ...criteria },
      holder: searchHolder,
      insured: { ...searchHolder }
    };
  }

  setCurrentClient(client: AgricolaClientInfo | null): void {
    this._currentClient = client;

    if (client) {
      this.ensureCurrentInsuranceInfo();
      const clientHolder = this.buildHolderFromClientInfo(client);
      this.currentInsuranceInfo = {
        ...this.currentInsuranceInfo,
        client_info: { ...client },
        holder: clientHolder,
        insured: { ...clientHolder },
        intermediary_code: this.toStringValue(client['intermediary_code'] || this.currentInsuranceInfo.intermediary_code)
      };
    }
  }

  // TODO: Connect when Agricola dashboard APIs are available.
  getDashboardCards(): Observable<AgricolaDashboardCard[]> {
    return of([
      {
        id: 1,
        title: 'Aperturas',
        description: 'Asesoría y ventas',
        image: 'assets/agricola/d12dc4d3b7e5bc1ffec40c4d8e5ce982bb55ab88.jpg',
        link: '#',
        isNew: true
      },
      {
        id: 2,
        title: 'Gestion y Reportes',
        description: 'Generacion de reportes',
        image: 'assets/agricola/262ed8e9c8004ba6af3be33d2cc3d2213ec14660.jpg',
        link: '#',
        isNew: true
      },
      {
        id: 3,
        title: 'Consultas',
        description: 'Consulta de Poliza',
        image: 'assets/agricola/217bbe992baa1b243b5b77e55de5d51f9a799483.jpg',
        link: '#',
        isNew: false
      },
      {
        id: 4,
        title: 'Aperturas Telemarketing',
        description: 'Captura por llamada (emisión diferida)',
        image: 'assets/agricola/d12dc4d3b7e5bc1ffec40c4d8e5ce982bb55ab88.jpg',
        link: '#',
        isNew: true
      }
    ]);
  }

  // TODO: Connect when Agricola coverage APIs are available.
  getCoverageOptions(): Observable<AgricolaCoverageOption[]> {
    return of([
      { name: 'Asesuisa', color: 'blue' },
      { name: 'CHUBB', color: 'orange' }
    ]);
  }

  getProducts(): Observable<AgricolaProduct[]> {
    return this.trackLoading('products', this.api.agricola.products()
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeProducts(response)),
        tap(products => { this.currentProducts = products; })
      ));
  }

  // --- Sell report (Reporte de ventas) ---

  getSellReport(filter: SellReportFilter, page: number, perPage = 50): Observable<SellReportResponse> {
    return this.api.agricola.reports(filter as unknown as Record<string, unknown>, page, perPage)
      .observable<{ data: SellReportResponse }>()
      .pipe(map(response => {
        const [rows, total] = this.unwrap<SellReportResponse>(response, [[], 0] as SellReportResponse);
        // The upstream report returns Estado as a string ("1"/"0"); coerce it to the
        // number the UI expects so the active/cancelled state renders correctly.
        const normalized = rows.map(row => ({ ...row, Estado: Number(row.Estado) }));
        return [normalized, total] as SellReportResponse;
      }));
  }

  exportSellReport(filter: SellReportFilter): Observable<ReportQueueItem> {
    return this.api.agricola.exportReport(filter as unknown as Record<string, unknown>)
      .observable<{ data: ReportQueueItem }>()
      .pipe(map(response => this.unwrap<ReportQueueItem>(response, {} as ReportQueueItem)));
  }

  getReportQueue(
    filter: { from_date?: string; to_date?: string; utcoffset?: number },
    page: number,
    perPage = 50
  ): Observable<ReportQueueResponse> {
    return this.api.agricola.reportsList(filter, page, perPage)
      .observable<{ data: ReportQueueResponse }>()
      .pipe(map(response => this.unwrap<ReportQueueResponse>(response, { data: [], total: 0 })));
  }

  attachmentDownloadUrl(attachmentId: number, mode = 1): string {
    return this.api.agricola.attachmentDownloadUrl(attachmentId, mode);
  }

  // Feature 1: audit record of the customer's signature for the seller review
  // screen. The BFF wraps the S2 `{ code, data }` envelope, so we unwrap twice:
  // the outer `data` is the S2 envelope, whose inner `data` is the record.
  async getSignEmissionLog(emissionLogId: number): Promise<SignEmissionLog> {
    const raw = await firstValueFrom(
      this.api.agricola.signEmissionLog(emissionLogId).observable<unknown>()
    );
    const envelope = this.unwrap<{ code?: number; data?: SignEmissionLog }>(raw, {});
    if (Number(envelope.code) !== 200 || !this.isRecord(envelope.data)) {
      throw new Error('Evidencia de firma no encontrada');
    }
    return envelope.data as SignEmissionLog;
  }

  // Feature 2: per-policy signature/certificate ids for the sell report menu.
  // Never rejects on "no evidence" — S2 returns has_signature=false with null ids,
  // which we normalize so the caller can just disable the signature options.
  async getPolicyDocuments(policyNumber: string): Promise<PolicyDocuments> {
    const fallback: PolicyDocuments = {
      policy_number: policyNumber,
      has_signature: false,
      signature_attachment_id: null,
      signed_document_attachment_id: null,
    };
    try {
      const raw = await firstValueFrom(
        this.api.agricola.policyDocuments(policyNumber).observable<unknown>()
      );
      const envelope = this.unwrap<{ code?: number; data?: PolicyDocuments }>(raw, {});
      if (Number(envelope.code) !== 200 || !this.isRecord(envelope.data)) {
        return fallback;
      }
      return { ...fallback, ...(envelope.data as PolicyDocuments) };
    } catch {
      return fallback;
    }
  }

  // The download endpoint requires the bearer token, so it must go through the
  // authenticated HTTP client (a plain browser navigation has no Authorization
  // header). Fetches the file as a blob and triggers a save in the browser.
  async downloadAttachment(attachmentId: number, mode = 1): Promise<void> {
    const response = await this.api.agricola.downloadAttachment(attachmentId, mode)
      .promise<HttpResponse<Blob>>();
    const blob = response.body;
    if (!blob) {
      return;
    }
    const filename = this.parseContentDispositionFilename(response.headers.get('Content-Disposition'))
      || `reporte-${attachmentId}`;
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  // Downloads the physical-signature ("contingencia") template. Goes through the
  // authenticated API proxy (bearer token required), so it's fetched as a blob and
  // saved in the browser — the raw GCS URL never reaches the client.
  async downloadContingencyTemplate(): Promise<void> {
    const response = await this.api.agricola.downloadContingencyTemplate()
      .promise<HttpResponse<Blob>>();
    const blob = response.body;
    if (!blob) {
      return;
    }
    const filename = this.parseContentDispositionFilename(response.headers.get('Content-Disposition'))
      || 'plantilla-contingencia.docx';
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  // Opens the "condicionado" (policy wording) PDF in a new tab. Goes through the
  // authenticated API proxy (bearer token required), so it's fetched as a blob and
  // shown via an object URL — the storage URL never reaches the client.
  // The wording can differ per plan, so the selected plan id travels alongside the
  // product code; upstream falls back to the product's own wording when the plan
  // does not declare one.
  async viewCondicionado(productSelection?: unknown, planId?: number | string): Promise<void> {
    const internalProductCode = this.resolveProductCode(productSelection);
    const response = await this.api.agricola.downloadCondicionado(internalProductCode, planId)
      .promise<HttpResponse<Blob>>();
    const blob = response.body;
    if (!blob) {
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }

  async viewAttachment(attachmentId: number): Promise<void> {
    const response = await this.api.agricola.downloadAttachment(attachmentId, 0)
      .promise<HttpResponse<Blob>>();
    const blob = response.body;
    if (!blob) {
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }

  // Fetches an attachment (e.g. the customer's signature) as a data: URL so the
  // caller can render it inline. A data: URL (not a blob: object URL) is used on
  // purpose: the app CSP allows `img-src ... data:` but not `blob:`, so an object
  // URL would be blocked when bound to an <img src>.
  async fetchAttachmentDataUrl(attachmentId: number): Promise<string> {
    const response = await this.api.agricola.downloadAttachment(attachmentId, 0)
      .promise<HttpResponse<Blob>>();
    const blob = response.body;
    if (!blob) {
      throw new Error('El adjunto no devolvió contenido.');
    }
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el adjunto.'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  }

  private parseContentDispositionFilename(header: string | null): string {
    if (!header) {
      return '';
    }
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1].trim()) : '';
  }

  buildSellReportFilter(
    fromDate: string,
    toDate: string,
    products: AgricolaProduct[],
    agencyCode?: string
  ): SellReportFilter {
    const codes = products.map(p => p.internal_product_code).filter(Boolean);
    const currentUser = this.meService?.currentUser;
    const agency = (agencyCode || '').trim();
    return {
      utcoffset: new Date().getTimezoneOffset(),
      from_date: fromDate,
      to_date: toDate,
      products: products.length
        ? products.map(p => ({ id: p.id, name: p.product_name, code: p.internal_product_code }))
        : null,
      product: products.length ? undefined : 'todos',
      product2: products.length === 1 ? products[0].product_name : undefined,
      lista_p: codes,
      ext_user_code: currentUser?.username || currentUser?.email,
      // Optional agency filter: only included when a specific agency is chosen so
      // the upstream report keeps its "all agencies" behaviour otherwise.
      agency_code: agency || undefined,
    };
  }

  // Unwrap the BFF `{ data: ... }` envelope, tolerating an already-unwrapped body.
  private unwrap<T>(response: unknown, fallback: T): T {
    if (this.isRecord(response) && 'data' in response) {
      const value = response['data'];
      return (value === undefined || value === null) ? fallback : (value as T);
    }
    return (response === undefined || response === null) ? fallback : (response as T);
  }

  getPlans(productSelection: unknown): Observable<AgricolaPlan[]> {
    const internalProductCode = this.resolveProductCodeFromSelection(productSelection);

    return this.trackLoading('plans', this.api.agricola.plans({ internal_product_code: internalProductCode })
      .observable<unknown>()
      .pipe(
        map(response => this.normalizePlans(response)),
        tap(plans => { this.currentPlans = plans; })
      ));
  }

  getAssistancePlans(productSelection: unknown): Observable<AgricolaPlan[]> {
    const payload: AgricolaAssistancePlansPayload = {
      company: ASSISTANCE_COMPANY,
      internal_product_code: this.resolveProductCodeFromSelection(productSelection)
    };

    return this.trackLoading('plans', this.api.agricola.assistancePlans(payload)
      .observable<unknown>()
      .pipe(
        map(response => this.normalizePlans(response)),
        tap(plans => { this.currentPlans = plans; })
      ));
  }

  validateAssistanceProduct(productSelection: unknown): Observable<AgricolaAssistanceValidation> {
    const internalProductCode = this.resolveProductCode(productSelection);

    return this.api.agricola.validateAssistanceProduct({ internal_product_code: internalProductCode })
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeAssistanceValidation(response)),
        tap(validation => {
          this._isAssistanceProduct = validation.is_assistance;
          if (!validation.is_assistance) {
            this._availableAddOns = [];
            this._selectedAddOns = [];
          }
        })
      );
  }

  getAssistanceAddOns(mainPlanId: number | string): Observable<AgricolaAddOn[]> {
    return this.api.agricola.getAssistanceAddOns({ plan: mainPlanId })
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeAddOns(response)),
        tap(addOns => { this._availableAddOns = addOns; })
      );
  }

  setSelectedAddOns(addOns: AgricolaAddOn[]): void {
    this._selectedAddOns = (addOns || []).map(addOn => ({ ...addOn }));
  }

  getAddOnsTotal(): number {
    return this._selectedAddOns.reduce((sum, addOn) => sum + (Number(addOn.price) || 0), 0);
  }

  clearAssistanceState(): void {
    this._isAssistanceProduct = false;
    this._availableAddOns = [];
    this._selectedAddOns = [];
  }

  resolveProductCodeFromSelection(value: unknown): string {
    return this.resolveProductCode(value);
  }

  getProductBySelection(value: unknown): AgricolaProduct | null {
    const selectedId = this.toStringValue(value).trim();
    const selectedCode = this.resolveProductCode(value);
    const product = this.currentProducts.find(item => String(item.id) === selectedId || item.internal_product_code === selectedCode);
    return product ? { ...product } : null;
  }

  // Assistance products from the loaded catalog (is_assistance === true), used to
  // render the selectable product boxes inside the premium step.
  getAssistanceProducts(): AgricolaProduct[] {
    return this.currentProducts.filter(product => product.is_assistance === true).map(product => ({ ...product }));
  }

  // Productos del catálogo del mismo tipo que el seleccionado (asistencias con
  // asistencias, seguros con seguros), para las chips de cambio rápido del paso
  // de prima.
  getProductsOfSameKind(isAssistance: boolean): AgricolaProduct[] {
    return this.currentProducts
      .filter(product => (product.is_assistance === true) === isAssistance)
      .map(product => ({ ...product }));
  }

  getCountries(): Observable<AgricolaCountry[]> {
    return this.trackLoading('countries', this.api.agricola.countries()
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeCountries(response)),
        tap(countries => { this.currentCountries = countries; })
      ));
  }

  getStates(countryId: string | number): Observable<AgricolaCatalogOption[]> {
    return this.trackLoading('states', this.api.agricola.states(countryId)
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeCatalogOptions(response)),
        tap(states => { this.currentStates = states; })
      ));
  }

  getSubstates(countryId: string | number, stateId: string | number): Observable<AgricolaCatalogOption[]> {
    return this.trackLoading('substates', this.api.agricola.substates(countryId, stateId)
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeCatalogOptions(response)),
        tap(substates => { this.currentSubstates = substates; })
      ));
  }

  getCities(countryId: string | number, substateId: string | number): Observable<AgricolaCatalogOption[]> {
    return this.trackLoading('cities', this.api.agricola.cities(countryId, substateId)
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeCatalogOptions(response)),
        tap(cities => { this.currentCities = cities; })
      ));
  }

  getIdentificationTypes(countryId: string | number): Observable<AgricolaCatalogOption[]> {
    return this.trackLoading('identificationTypes', this.api.agricola.identificationTypes(countryId)
      .observable<unknown>()
      .pipe(map(response => this.normalizeCatalogOptions(response))));
  }

  getGenders(): Observable<AgricolaCatalogOption[]> {
    return this.trackLoading('genders', this.api.agricola.genders()
      .observable<unknown>()
      .pipe(
        map(response => this.normalizeCatalogOptions(response)),
        tap(genders => { this.currentGenders = genders; })
      ));
  }

  getLocationsByProduct(productSelection: unknown): Observable<AgricolaCatalogOption[]> {
    const productId = this.toStringValue(productSelection).trim();

    return this.trackLoading('creationAddresses', this.api.agricola.locationsByProduct(productId)
      .observable<unknown>()
      .pipe(map(response => this.normalizeLocationOptions(response))));
  }

  searchClient(criteria: AgricolaClientSearchCriteria): Observable<AgricolaClientInfo> {
    this._lastClientSearch = { ...criteria };
    this.setCurrentClientSearch(criteria);

    const payload: AgricolaClientInfoPayload = {
      idbusqueda: SEARCH_TYPE_CODES[criteria.searchType] ?? '1',
      idcdatosb:  criteria.searchValue,
      idcagenci: SIS_AGRICOLA_DEFAULTS.agencyCode,
      idcusuari: SIS_AGRICOLA_DEFAULTS.userCode,
    };

    return this.trackLoading(['clientSearch', 'holder'], this.api.agricola.searchClient(payload)
      .headers(this.emissionCorrelationHeaders())
      .observable<AgricolaApiResponse<AgricolaClientInfo>>()
      .pipe(
        map(response => response.data),
        map(client => {
          // A 200 with an empty body (e.g. { data: {} }) means the client was
          // not found; surface it as an error so the caller shows the
          // "not found" dialog instead of navigating to the empty form.
          if (this.isClientInfoEmpty(client)) {
            throw new Error('CLIENT_NOT_FOUND');
          }
          return client;
        }),
        tap(client => {
          this.setCurrentClient(client);
          this._preloadedPaymentInfo = this.normalizePreloadedPaymentInfo(client);
        })
      ));
  }

  private isClientInfoEmpty(client: AgricolaClientInfo | null | undefined): boolean {
    if (!this.isRecord(client)) {
      return true;
    }

    const payload = this.clientInfoPayload(client);
    // Emptiness heuristic only: any identifier the response may carry counts as
    // "found", so the número único aliases belong here (unlike in the holder's
    // identification document, see buildHolderFromClientInfo).
    const identification = this.firstClientValue(payload, [
      'identification_id',
      'numUnico',
      'idUnico',
      'numeroUnico',
      'customerNumber',
      'numDocumento',
      'documentNumber',
      'document_number',
      'dui',
      'nit'
    ]);
    const name = this.firstClientValue(payload, [
      'name',
      'fullName',
      'full_name',
      'nombreCompleto',
      'clientName',
      'customerName'
    ]);

    return !this.toStringValue(identification).trim() && !this.toStringValue(name).trim();
  }

  buildClientAccountsPayload(idUnico: unknown): AgricolaClientAccountsPayload {
    return {
      idUnico: this.toStringValue(idUnico),
      codAgencia: SIS_AGRICOLA_DEFAULTS.agencyCode,
      codUsuario: SIS_AGRICOLA_DEFAULTS.userCode
    };
  }

  resolveClientUniqueNumberForWizard(wizardFormValue: any): string {
    return this.resolveClientUniqueNumber(wizardFormValue);
  }

  getClientAccounts(idUnico: unknown): Observable<unknown> {
    return this.trackLoading('clientAccounts', this.api.agricola.getClientAccounts(this.buildClientAccountsPayload(idUnico))
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>());
  }

  getAgencies(): Observable<unknown> {
    return this.api.agricola.agencies().observable<unknown>();
  }

  // Normalized agency catalog for dropdowns: value=code, label=localized name.
  getAgencyOptions(): Observable<{ value: string; label: string }[]> {
    return this.getAgencies().pipe(
      map(response => this.extractCatalogRows(response)
        .filter((row): row is Record<string, unknown> => this.isRecord(row))
        .map(row => ({
          value: this.toStringValue(row['code']),
          label: this.toStringValue(row['description_es'] || row['description_en'] || row['code'])
        }))
        .filter(opt => !!opt.value))
    );
  }

  buildBalanceValidationPayload(wizardFormValue: any): AgricolaBalanceValidatePayload {
    const step3 = wizardFormValue?.step3 || {};
    const step5 = wizardFormValue?.step5 || {};
    const premiumData = step3.premiumData || {};
    const paymentInfo = step5.paymentInfo || {};
    const sellerInfo = step5.sellerInfo || {};

    return {
      numUnico: this.resolveClientUniqueNumber(wizardFormValue),
      tipoCuenta: this.balanceValidationTypeCode(paymentInfo.accountType),
      numCuenta: this.toStringValue(paymentInfo.accountNumber).replace(/\*/g, ''),
      valPrima: this.formatSisMoney(premiumData.premiumResult),
      agencia: this.toStringValue(sellerInfo.agencyCode) || SIS_AGRICOLA_DEFAULTS.agencyCode,
      usuario: SIS_AGRICOLA_DEFAULTS.userCode
    };
  }

  validateBalance(payload: AgricolaBalanceValidatePayload): Observable<unknown> {
    return this.api.agricola.validateBalance(payload)
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  buildSellerValidationPayload(wizardFormValue: any): AgricolaSellerValidatePayload {
    const sellerInfo = wizardFormValue?.step5?.sellerInfo || {};

    return {
      codigoVendedor: this.toStringValue(sellerInfo.sellerCode),
      tipoVendedor: this.toStringValue(sellerInfo.sellerType)
    };
  }

  validateSeller(payload: AgricolaSellerValidatePayload): Observable<unknown> {
    return this.api.agricola.validateSeller(payload)
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  // Pulls the validated seller name from a /sellers/validate response. The
  // upstream returns it at data.body.nombreVendedor, but S2 sometimes wraps it
  // an extra level (data.data.body.nombreVendedor); checks both depths and
  // returns '' when absent so callers can decide whether to prefill the
  // seller-name field.
  extractSellerName(response: unknown): string {
    const data = this.valueFromRecord(response, 'data');
    const nestedData = this.valueFromRecord(data, 'data');
    const body = [
      this.valueFromRecord(data, 'body'),
      this.valueFromRecord(nestedData, 'body')
    ].find(value => this.isRecord(value));

    if (this.isRecord(body)) {
      return this.toStringValue(body['nombreVendedor']).trim();
    }
    return '';
  }

  buildCreatePolicyPayload(wizardFormValue: any, numPoliza = ''): AgricolaCreatePolicyPayload {
    const step1 = wizardFormValue?.step1 || {};
    const step3 = wizardFormValue?.step3 || {};
    const step5 = wizardFormValue?.step5 || {};
    const productSelection = step1.productSelection || {};
    const premiumData = step3.premiumData || {};
    const paymentInfo = step5.paymentInfo || {};
    const sellerInfo = step5.sellerInfo || {};

    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const planPrefix = this.toStringValue(
      selectedPlan?.config?.['prefix'] || selectedPlan?.product_config?.['prefix']
    ).replace(/\D/g, '');

    return {
      numUnico: this.resolveClientUniqueNumber(wizardFormValue),
      numPoliza: this.stripPolicyNumberPrefix(numPoliza),
      numPolizaOriginal: this.toStringValue(numPoliza),
      codProPoliza: planPrefix || this.resolveSisProductCode(productSelection.product, premiumData.planOption),
      codAliado: this.resolveAllyCode(productSelection.ally),
      opcSeguro: SIS_AGRICOLA_DEFAULTS.insuranceOption,
      cuentaTDC: this.toStringValue(paymentInfo.accountNumber).replace(/\*/g, ''),
      tipoCobro: this.balanceValidationTypeCode(paymentInfo.accountType),
      multiCobro: this.resolveMultiCobro(paymentInfo),
      prima: this.formatSisMoney(premiumData.premiumResult),
      sumaAsegurada: SIS_AGRICOLA_DEFAULTS.defaultInsuredAmount,
      dpd: this.toStringValue(sellerInfo.sellerCode)
    };
  }

  // SIS expects only the sequential part of the policy number (e.g. "1258"),
  // without the "codProPoliza-" prefix the partner sale response returns
  // (e.g. "0502-1258").
  private stripPolicyNumberPrefix(numPoliza: string): string {
    const value = this.toStringValue(numPoliza);
    const dashIndex = value.lastIndexOf('-');
    return dashIndex >= 0 ? value.slice(dashIndex + 1) : value;
  }

  // multiCobro "Y" = the partner may retry the charge against multiple accounts.
  // Driven only by the explicit "acceptsMulticharge" answer; autoDebitAuthorized
  // is a separate, always-required consent to charge the account and must not
  // be OR'd in here or "No" answers would always be sent as "Y".
  private resolveMultiCobro(paymentInfo: Record<string, unknown>): string {
    return this.acceptsMultichargeValue(paymentInfo['acceptsMulticharge']) === '1' ? 'Y' : 'N';
  }

  getRelationshipOptions(strategy: RelationshipCatalogStrategy, productCode?: string): Observable<RelationshipOption[]> {
    const request = strategy === 'product-specific'
      ? this.api.relationships.productSpecific(productCode || '')
      : strategy === 'public'
        ? this.api.relationships.public()
        : this.api.relationships.global();

    return this.trackLoading('relationships', request.observable<unknown>().pipe(
      map(response => this.normalizeRelationshipOptions(response, strategy))
    ));
  }

  // Header carrying the correlation id shared across every request of a single
  // policy emission flow (client lookup → balance → seller → accounts → policy).
  private emissionCorrelationHeaders(): Record<string, string> {
    return { 'X-Correlation-Id': this.ensureEmissionCorrelationId() };
  }

  private ensureEmissionCorrelationId(): string {
    if (!this._emissionCorrelationId) {
      this._emissionCorrelationId = this.generateCorrelationId();
    }
    return this._emissionCorrelationId;
  }

  private generateCorrelationId(): string {
    const cryptoRef = globalThis.crypto;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
      const random = (Math.random() * 16) | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  private trackLoading<T>(keys: AgricolaLoadingKey | AgricolaLoadingKey[], source: Observable<T>): Observable<T> {
    const loadingKeys = Array.isArray(keys) ? keys : [keys];
    this.setLoading(loadingKeys, true);

    return source.pipe(finalize(() => {
      this.setLoading(loadingKeys, false);
    }));
  }

  private setLoading(keys: AgricolaLoadingKey[], value: boolean): void {
    const current = this.loadingStateSubject.value;
    const next = { ...current };
    keys.forEach(key => {
      next[key] = value;
    });
    this.loadingStateSubject.next(next);
  }

  setCurrentInsuranceBeneficiary(beneficiaries: InsuranceBeneficiary[]): void {
    this.ensureCurrentInsuranceInfo();

    const cleanBeneficiaries = beneficiaries.map(beneficiary => ({ ...beneficiary }));

    if (cleanBeneficiaries.length) {
      this.currentInsuranceInfo.beneficiarytable = cleanBeneficiaries;
    } else {
      delete this.currentInsuranceInfo.beneficiarytable;
    }
  }

  setCurrentInsuranceDependent(dependents: InsuranceDependent[]): void {
    this.ensureCurrentInsuranceInfo();

    const clean = this.filterValidDependents(dependents);
    if (clean.length > 0) {
      this.currentInsuranceInfo.dependents = clean;
    } else {
      delete this.currentInsuranceInfo.dependents;
    }
  }

  getCurrentInsuranceDependent(): InsuranceDependent[] {
    return [...(this.currentInsuranceInfo.dependents || [])];
  }

  setCurrentInsuranceHolder(holder: AgricolaInsuranceHolder): void {
    this.ensureCurrentInsuranceInfo();

    const cleanHolder = { ...holder };
    this.currentInsuranceInfo = {
      ...this.currentInsuranceInfo,
      holder: cleanHolder,
      insured: { ...cleanHolder }
    };
  }

  setCurrentInsurancePayment(paymentInfo: AgricolaInsurancePayment): void {
    this.ensureCurrentInsuranceInfo();

    this.currentInsuranceInfo = {
      ...this.currentInsuranceInfo,
      payment_info: {
        ...(this.currentInsuranceInfo.payment_info || { policy_charge: 0 }),
        ...paymentInfo,
        frequency: paymentInfo.frequency || 'E'
      }
    };
  }

  // payment_type para el core de S2: 0 = débito, 1 = crédito (única escala que
  // define backend/schemas.py en payment_info). La entrada es el CodTipoCta de
  // Agrícola (1=corriente, 2=ahorro, 3=TDC), que es OTRA escala y choca en el 1
  // y el 2: mapearla de forma directa mandaba la corriente como crédito y la
  // tarjeta como débito. Bajo una escala 0/1 sólo la tarjeta es crédito y
  // cualquier cuenta bancaria es débito; la distinción corriente/ahorro no se
  // pierde, viaja intacta en account_type.
  //
  // Un tipo vacío o desconocido cae en débito por definición (no hay un tercer
  // valor válido, y el schema del core no admite null).
  resolveAgricolaPaymentType(accountType: unknown): number {
    return this.isCreditCardAccount(accountType) ? 1 : 0;
  }

  // Única definición de "esto es una tarjeta" en todo el flujo: la misma que
  // resuelve tipoCuenta (/balance/validate) y tipoCobro (/policies), para que
  // los tres campos no puedan contradecirse entre sí. Pública porque el paso de
  // pago también la consulta para saltarse la validación de fondos en TDC.
  isCreditCardAccount(accountType: unknown): boolean {
    return this.balanceValidationTypeCode(accountType) === AGRICOLA_TDC_CHARGE_CODE;
  }

  updateCurrentInsuranceFromWizard(wizardFormValue: any): Partial<InsuranceSalePayload> {
    this.ensureCurrentInsuranceInfo();

    const step1 = wizardFormValue?.step1 || {};
    const step3 = wizardFormValue?.step3 || {};
    const step4 = wizardFormValue?.step4 || {};
    const step5 = wizardFormValue?.step5 || {};
    const mainData = step1.mainData || {};
    const complementary = step1.complementary || {};
    const productSelection = step1.productSelection || {};
    const pep = step1.pep || {};
    const premiumData = step3.premiumData || {};
    const paymentInfo = step5.paymentInfo || {};
    const sellerInfo = step5.sellerInfo || {};

    const holder = this.buildInsuranceHolder(mainData, complementary);
    const beneficiaries = this.toInsuranceBeneficiaries(
      this.currentInsuranceInfo.beneficiarytable || step4.beneficiaries || []
    );
    const payment = this.buildPaymentInfo(paymentInfo, premiumData, holder);
    const sellerCode = this.toStringValue(sellerInfo.sellerCode).trim();
    const selectedProductId = this.toStringValue(productSelection.product).trim();
    const selectedProductCode = this.resolveProductCode(productSelection.product);
    // The date field emits dd/MM/yyyy when picked from the calendar, so it has to
    // be normalized before it becomes date_effective (an ISO date upstream).
    const effectiveDate = this.toIsoDate(complementary['effectiveDate']);

    this.currentInsuranceInfo = {
      ...this.currentInsuranceInfo,
      product_code: selectedProductCode,
      new_id_product: selectedProductId,
      product_id: selectedProductId,
      insurance_plan_id: this.planIdFromValue(premiumData.planOption),
      creation_address: 'sedes_s2plus_agricola_sis',
      date_issued: this.currentInsuranceInfo.date_issued || this.todayIsoDate(),
      date_effective: effectiveDate || this.currentInsuranceInfo.date_effective || this.todayIsoDate(),
      self_insurance: true,
      holder,
      insured: { ...holder },
      payment_info: payment,
      seller_id: sellerCode || this.currentInsuranceInfo.seller_id || null,
      policy_number: this.currentInsuranceInfo.policy_number || '',
      pep_info: { ...pep },
      seller_info: { ...sellerInfo }
    };

    if (beneficiaries.length) {
      this.currentInsuranceInfo.beneficiarytable = beneficiaries;
    } else {
      delete this.currentInsuranceInfo.beneficiarytable;
    }

    const dependents = this.currentInsuranceInfo.dependents || [];
    if (dependents.length) {
      this.currentInsuranceInfo.dependents = dependents;
    } else {
      delete this.currentInsuranceInfo.dependents;
    }

    if (selectedProductCode === 'AGRICOLA_FRE_CYBER_MUVIT') {
      const destinations = this.buildDestinations(complementary);
      if (destinations.length) {
        this.currentInsuranceInfo.destinations = destinations;
      }
    } else {
      delete this.currentInsuranceInfo.destinations;
    }

    return this.currentInsuranceInfo;
  }

  async buildInsuranceSalePayload(wizardFormValue: any): Promise<InsuranceSalePayload> {
    const current = this.updateCurrentInsuranceFromWizard(wizardFormValue);
    const today = this.todayIsoDate();
    const holder = this.withNormalizedPhones(current.holder || { identification_id: '', name: '' });
    const insured = current.insured ? this.withNormalizedPhones(current.insured) : { ...holder };
    const paymentInfo = current.payment_info || { policy_charge: 0, frequency: 'E' };

    const payload: InsuranceSalePayload = {
      finalUrl: this.toStringValue(current['finalUrl'] || ''),
      product_code: this.toStringValue(current.product_code || 'AGRICOLA_FRE_CYBER'),
      insurance_plan_id: Number(current.insurance_plan_id) || 1,
      creation_address: 'sedes_s2plus_agricola_sis',
      date_issued: this.toStringValue(current.date_issued || today),
      date_effective: this.toStringValue(current.date_effective || today),
      self_insurance: true,
      holder,
      insured,
      payment_info: await this.cleanSalesPaymentInfo(paymentInfo, holder),
      intermediary_code: this.toStringValue(current.intermediary_code),
      policy_number: this.toStringValue(current.policy_number),
      seller_id: this.toStringValue(current.seller_id)
    };

    const beneficiaries = this.filterValidBeneficiaries(this.toInsuranceBeneficiaries(current.beneficiarytable || []));
    if (beneficiaries.length) {
      payload.beneficiarytable = beneficiaries;
    } else {
      delete payload.beneficiarytable;
    }

    const dependents = this.filterValidDependents(current.dependents || []);
    if (dependents.length) {
      payload.dependents = dependents.map(d => ({ ...d }));
    } else {
      delete payload.dependents;
    }

    if (current.destinations?.length) {
      payload.destinations = current.destinations.map(destination => ({ ...destination }));
    }

    this.currentInsuranceInfo = { ...payload };

    return payload;
  }

  async buildAssistanceDataToSend(
    wizardFormValue: any,
    options?: { origin?: string; finalUrl?: string; sentChannel?: 'email' | 'sms' }
  ): Promise<AssistanceDataToSend> {
    const current = this.updateCurrentInsuranceFromWizard(wizardFormValue);
    const today = this.todayIsoDate();
    const holder = current.holder || { identification_id: '', name: '' };
    const paymentInfo = current.payment_info || { policy_charge: 0, frequency: 'E' };
    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const planConfig = this.resolveAssistancePlanConfig(selectedPlan);
    const singleClientAssistancePlan = this.resolveSingleClientAssistancePlan(selectedPlan);
    const planId = this.resolveAssistancePlanId(selectedPlan);
    const issuedDate = this.toStringValue(current.date_issued || today);
    const effectiveDate = this.toStringValue(current.date_effective || issuedDate);
    const creditNumber = this.toStringValue(paymentInfo.account_number).replace(/\*/g, '');
    const cardType = this.balanceValidationTypeCode(paymentInfo.account_type);
    const isCreditCard = this.isCreditCardAccount(paymentInfo.account_type);
    const cardSubtype = this.acceptsMultichargeValue(wizardFormValue?.step5?.paymentInfo?.acceptsMulticharge);
    const autoDebitValue = wizardFormValue?.step5?.paymentInfo?.autoDebitAuthorized ? '1' : '0';
    const numUnico = this.resolveClientUniqueNumber(wizardFormValue);
    const sellerInfo = (current['seller_info'] || {}) as Record<string, unknown>;
    const sellerName = this.toStringValue(sellerInfo['sellerName']);
    const agencyCode = this.toStringValue(sellerInfo['agencyCode']);
    const agencyName = this.toStringValue(sellerInfo['agencyName']);
    const customer: Record<string, unknown> = {
      type: 'MI',
      nationalId: this.toStringValue(holder.identification_id),
      firstName: this.toStringValue(holder.first_name),
      middleName: this.toStringValue(holder.middle_name),
      lastName: this.toStringValue(holder.last_name),
      secondLastName: this.toStringValue(holder.last_name_2),
      birthDate: this.toIsoDate(holder.birthday),
      emailAddress: this.toStringValue(holder.email),
      // El fijo manda, pero sólo si es un número: cuando S2 lo devuelve como
      // puro relleno el titular queda sin phone1 y el celular toma su lugar.
      phoneNumber1: this.firstRealPhone(holder.phone1, holder.mobile) || AGRICOLA_EMPTY_PHONE,
      phoneNumber2: this.normalizePhoneNumber(holder.phone2),
      address: this.toStringValue(holder.address),
      gender: await this.genderToS2(holder.gender),
      company: '',
      relationship: 0
    };

    const dataToSend: AssistanceDataToSend = {
      transactionType: 'EMI',
      transactionId: numUnico,
      tranTypeDescription: 'Emisión de asistencia Multiasistencia Basica y Vip',
      product_code: this.toStringValue(current.product_code || 'AGRICOLA_FRE_CYBER'),
      countryId: 'SV',
      planId,
      insurance_plan_id: selectedPlan?.insurance_plan_id ?? current.insurance_plan_id,
      creation_address: 'sedes_s2plus_agricola_sis',
      sponsorId: this.toStringValue(this.getProductBySelection(current.product_code)?.['sponsor_code'] || 'BAES'),
      sellerId: this.toStringValue(current.seller_id),
      issuedDate,
      tranDate: issuedDate,
      date_issued: issuedDate,
      date_effective: effectiveDate,
      premium: this.moneyAmount(wizardFormValue?.step3?.premiumData?.premiumResult ?? paymentInfo.premium),
      origin: this.toStringValue(options?.origin || 's2_cuentame'),
      salesChannel: 'TMK',
      salesSubchannel: 'WH',
      beneficiaryName: 'null',
      stateId: 1,
      districtId: 4,
      customers: [customer],
      extraData: [
        { key: 'document_type', value: this.toStringValue(holder.identification_type) },
        { key: 'credit_number', value: creditNumber },
        { key: 'card_subtype', value: cardSubtype },
        { key: 'card_type', value: cardType },
        { key: 'auto_debit_authorized', value: autoDebitValue },
        { key: 'unique_number', value: numUnico },
        { key: 'seller_type', value: this.toStringValue(sellerInfo['sellerType']) },
        { key: 'seller_code', value: this.toStringValue(sellerInfo['sellerCode']) },
        { key: 'seller_name', value: sellerName },
        { key: 'agency_code', value: agencyCode },
        { key: 'agency_name', value: agencyName },
        { key: 'created_from', value: 'ext_agricola' }
      ],
      additionalData: {
        credit_number: creditNumber,
        card_type: cardType,
        card_subtype: cardSubtype,
        account: numUnico,
        auto_debit_authorized: autoDebitValue,
        salesman_name: sellerName,
        location_id: agencyCode,
        location_name: agencyName,
        // El patrocinador sólo confirma lo que él mismo cobra. En TDC la prima
        // la cobra la tarjeta, no el banco, así que la clave se omite por
        // completo (mandarla en null la rechazaría el schema de apolo) y es esa
        // ausencia la que hace que ext_agricola derive sponsor_confirmed
        // server-side.
        ...(isCreditCard ? {} : { sponsor_confirmed: 1 })
      },
      plan_config: planConfig ? { ...planConfig } : undefined,
      addons: this._selectedAddOns.map(addOn => addOn.assistance_plan_id)
    };

    if (singleClientAssistancePlan !== null) {
      dataToSend.single_client_assistance_plan = singleClientAssistancePlan;
    }

    // When the sale is emitted through the private link, record how the signature
    // request was sent (email/sms) and the contacts used, so S2 can persist them on
    // client_sign_emission_log (sent_channel / sent_to_email / sent_to_phone).
    if (options?.sentChannel) {
      dataToSend.extraData.push(
        { key: 'sent_channel', value: options.sentChannel },
        { key: 'sent_to_email', value: this.resolvePrivateLinkEmail(wizardFormValue, holder) },
        { key: 'sent_to_phone', value: this.resolvePrivateLinkPhone(wizardFormValue, holder) }
      );
    }

    const dependents = this.filterValidDependents(current.dependents || []);
    if (dependents.length) {
      dataToSend.dependents = dependents.map(dependent => ({ ...dependent }));
    } else {
      delete dataToSend.dependents;
    }

    if (!dataToSend.addons?.length) {
      delete dataToSend.addons;
    }

    if (!dataToSend.plan_config) {
      delete dataToSend.plan_config;
    }

    const finalUrl = this.toStringValue(options?.finalUrl);
    if (finalUrl) {
      dataToSend['finalUrl'] = finalUrl;
      this.currentInsuranceInfo['finalUrl'] = finalUrl;
    }

    return dataToSend;
  }

  createInsuranceSale(payload: InsuranceSalePayload, collectedData: unknown): Observable<unknown> {
    return this.api.agricola.createInsuranceSale(this.buildEmissionBody(payload, collectedData))
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  getAssistanceSaleById(code: string): Observable<any> {
    return this.api.agricola.assistanceById(code).observable<any>();
  }

  chubbTransaction(payload: any): Observable<any> {
    return this.api.agricola.chubbTransaction(payload).observable<any>();
  }

  getAssistanceSaleExtraData(assistanceSaleId: number): Observable<any> {
    return this.api.agricola.getAssistanceSaleExtraData(assistanceSaleId).observable<any>();
  }

  getAssistanceSaleExtraInfo(assistanceSaleId: number): Observable<any> {
    return this.api.agricola.getAssistanceSaleExtraInfo(assistanceSaleId).observable<any>();
  }


  getAssistancePaymentRequirement(assistanceSaleId: number): Observable<any> {
    return this.api.agricola.getAssistancePaymentRequirement({ assistance_sale_id: assistanceSaleId }).observable<any>();
  }

  getChubbTransactions(saleId: number, code: string, personalId: string): Observable<any> {
    return this.api.agricola.getChubbTransactions({
      transaction:              'ALL',
      is_assistance_sale:       true,
      policy_assistance_code:   saleId,
      is_multiple_transactions: false,
      country_id:               4,
      sponsor_id:               'ESR98',
      code,
      personal_id:              personalId,
      userid:                   this.meService.currentUser?.id ?? '1703',
      guid:                     '',
      elapsedTime:              0
    }).observable<any>();
  }

  searchPolicies(value: string, page = 1, perPage = 15, typeValue = 9): Observable<any> {
    const payload: ApoloContactSearchPayload = {
      country_id: 4,
      country_sap_code: 'SV', // SALVADOR
      sponsor_id: 'ESR98', // AGRICOLA 
      value,
      type_value: typeValue,
      page,
      per_page: perPage
    };
    return this.api.agricola.searchContactApolo(payload).observable<any>();
  }

  createAssistanceSale(payload: AssistanceDataToSend, collectedData: unknown): Observable<unknown> {
    return this.api.agricola.createAssistanceSale(this.buildEmissionBody(payload, collectedData))
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  // Single-client uniqueness prevalidation. Runs after the balance/seller checks and
  // before the private link is sent or the contingency sale is emitted, so a client
  // that already has a colliding active sale is blocked here instead of failing later
  // with Apolo's "Sale already placed". S2 core is the source of truth (it reads the
  // uniqueness flags from the plan/product config), so we just forward the sale
  // payload. Fail-closed: any non-200 (business 400 or upstream timeout/5xx) rejects,
  // and the caller must not proceed to emit.
  async prevalidateAssistanceSale(wizardFormValue: any): Promise<void> {
    const payload = await this.buildAssistanceDataToSend(wizardFormValue);
    try {
      await firstValueFrom(
        this.api.agricola.prevalidateAssistanceSale(payload).observable<unknown>()
      );
    } catch (error: any) {
      throw new Error(this.extractPrevalidationError(error));
    }
  }

  // Prefer S2 core's business message (duplicate-sale text) which the BFF nests under
  // upstream_response.message; fall back to a generic fail-closed message for infra
  // errors (timeout/5xx) so we never leak the raw upstream error to the seller.
  private extractPrevalidationError(error: any): string {
    const upstreamMessage = error?.error?.upstream_response?.message;
    if (typeof upstreamMessage === 'string' && upstreamMessage.trim()) {
      return upstreamMessage.trim();
    }
    return 'No se pudo validar la venta con el sistema. Intenta de nuevo en unos momentos.';
  }

  isAssistancePrivateLinkConfigured(): boolean {
    return !this.assistancePrivateLinkSetupError();
  }

  assistancePrivateLinkSetupError(): string {
    if (!this.assistancePaymentPublicUrl()) {
      return 'Falta configurar assistancePaymentPublicUrl para generar el enlace público de firma.';
    }

    if (!this.firebaseSync?.isConfigured()) {
      return 'Falta configurar Firebase para esperar la finalización de la asistencia con firma.';
    }

    return '';
  }

  requiresAssistanceDigitalSignature(wizardFormValue: any): boolean {
    if (!this._isAssistanceProduct) {
      return false;
    }

    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const selectedProduct = this.resolveSelectedProduct(wizardFormValue);
    const candidates = [
      wizardFormValue?.step3?.premiumData?.digital_signature,
      wizardFormValue?.step3?.premiumData?.digitalSignature,
      selectedPlan?.config?.['digital_signature'],
      selectedPlan?.config?.['digitalSignature'],
      selectedPlan?.product_config?.['digital_signature'],
      selectedPlan?.product_config?.['digitalSignature'],
      selectedProduct?.config?.['digital_signature'],
      selectedProduct?.config?.['digitalSignature'],
      this.currentInsuranceInfo['digital_signature'],
      this.currentInsuranceInfo['digitalSignature']
    ];

    return candidates.some(value => this.coerceBoolean(value) === true);
  }

  defaultAssistanceSignatureFlow(wizardFormValue: any): AgricolaAssistanceSignatureFlow {
    return this.requiresAssistanceDigitalSignature(wizardFormValue) ? 'private-link' : 'contingency';
  }

  // Opt-in flag (validate_signature_by_seller): when set on the product/plan the
  // customer only signs from the public link; the seller reviews the signature
  // here and emits. Read from the same config sources as the digital-signature
  // flag. The flag travels to the S2 public page inside the private-link request
  // (planInfo config), so no extra plumbing is needed on the send side.
  validatesSignatureBySeller(wizardFormValue: any): boolean {
    if (!this._isAssistanceProduct) {
      return false;
    }

    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const selectedProduct = this.resolveSelectedProduct(wizardFormValue);
    const candidates = [
      wizardFormValue?.step3?.premiumData?.validate_signature_by_seller,
      wizardFormValue?.step3?.premiumData?.validateSignatureBySeller,
      selectedPlan?.config?.['validate_signature_by_seller'],
      selectedPlan?.config?.['validateSignatureBySeller'],
      selectedPlan?.product_config?.['validate_signature_by_seller'],
      selectedPlan?.product_config?.['validateSignatureBySeller'],
      selectedProduct?.config?.['validate_signature_by_seller'],
      selectedProduct?.config?.['validateSignatureBySeller'],
      this.currentInsuranceInfo['validate_signature_by_seller'],
      this.currentInsuranceInfo['validateSignatureBySeller']
    ];

    return candidates.some(value => this.coerceBoolean(value) === true);
  }

  // Opt-in flag (only_tdc_accounts): when set on the product config the payment
  // step only offers the client's credit-card accounts, hiding checking/savings.
  // Read from the product config (this is a product-scoped concern — Antifraude+
  // is charged through the TDC billing trama, never a bank account). Uses the same
  // resolveSelectedProduct + coerceBoolean pattern as the signature flags above.
  onlyTdcAccounts(wizardFormValue: any): boolean {
    const selectedProduct = this.resolveSelectedProduct(wizardFormValue);
    const candidates = [
      selectedProduct?.config?.['only_tdc_accounts'],
      selectedProduct?.config?.['onlyTdcAccounts']
    ];

    return candidates.some(value => this.coerceBoolean(value) === true);
  }

  async createAssistancePrivateLink(
    wizardFormValue: any,
    channel?: 'email' | 'sms'
  ): Promise<AgricolaAssistancePrivateLinkState> {
    const token = this.generatePrivateLinkToken();
    const finalUrl = this.buildAssistancePrivateLinkUrl(token);
    const payload = await this.buildAssistancePrivateLinkPayload(wizardFormValue, token, finalUrl, channel);
    const response = await firstValueFrom(this.api.agricola.createAssistancePrivateLink(payload)
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>());
    return this.normalizeAssistancePrivateLinkState(response, token, finalUrl);
  }

  getAssistancePrivateLink(token: string): Observable<unknown> {
    return this.api.agricola.getAssistancePrivateLink(token)
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  updateAssistancePrivateLink(token: string, payload: { policy_number: string }): Observable<unknown> {
    return this.api.agricola.updateAssistancePrivateLink(token, payload)
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  sendAssistancePrivateLinkEmail(wizardFormValue: any, finalUrl: string): Observable<unknown> {
    return this.api.agricola.sendAssistancePrivateLinkEmail(
      this.buildAssistancePrivateLinkNotification(wizardFormValue, finalUrl, 'email')
    )
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  sendAssistancePrivateLinkSms(wizardFormValue: any, finalUrl: string): Observable<unknown> {
    return this.api.agricola.sendAssistancePrivateLinkSms(
      this.buildAssistancePrivateLinkNotification(wizardFormValue, finalUrl, 'sms')
    )
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  watchAssistancePrivateLinkCompletion(emissionLogId: number | string): Observable<AgricolaAssistanceFirebaseStatus> {
    if (!this.firebaseSync) {
      return new Observable(subscriber => {
        subscriber.error(new Error('Firebase no está disponible en esta ejecución.'));
      });
    }

    return this.firebaseSync.watchAssistanceCompletion(emissionLogId, 'externo');
  }

  // Seller-driven emission (validate_signature_by_seller). The upstream endpoint
  // reuses the signature the customer already stored and runs the Apolo sale; the
  // response carries policy_number/download_url/transaction_id for the summary and
  // for the Firebase "emitted" write that releases the customer's waiting page.
  emitAssistancePrivateLink(token: string): Promise<unknown> {
    return firstValueFrom(
      this.api.agricola.emitAssistancePrivateLink(token)
        .headers(this.emissionCorrelationHeaders())
        .observable<unknown>()
    );
  }

  async writeAssistanceEmissionResult(
    emissionLogId: number | string,
    result: { policyNumber: string; documentUrl: string; transactionId: string }
  ): Promise<void> {
    if (!this.firebaseSync) {
      throw new Error('Firebase no está disponible en esta ejecución.');
    }
    await this.firebaseSync.writeAssistanceEmissionResult(emissionLogId, result, 'externo');
  }

  // Seller rejects the signature: reset the link upstream (status 2 -> 0) so the
  // customer can sign again, then flip Firebase to pending so their waiting page
  // returns to the signature step.
  rejectAssistancePrivateLink(token: string, reason: string): Promise<unknown> {
    return firstValueFrom(
      this.api.agricola.rejectAssistancePrivateLink(token, { reason })
        .headers(this.emissionCorrelationHeaders())
        .observable<unknown>()
    );
  }

  async writeAssistanceRejection(emissionLogId: number | string, reason: string): Promise<void> {
    if (!this.firebaseSync) {
      throw new Error('Firebase no está disponible en esta ejecución.');
    }
    await this.firebaseSync.writeAssistanceRejection(emissionLogId, reason, 'externo');
  }

  // Reads the response envelope of emitAssistancePrivateLink, tolerating the
  // nested `data` wrappers the proxy may add. The upstream may report a failed
  // emission with HTTP 200 by embedding an `error` field (e.g. "Sale already
  // placed."); surface it as a thrown error so callers keep the review screen
  // instead of advancing to the success summary.
  extractEmissionResult(response: unknown): { policyNumber: string; documentUrl: string; transactionId: string } {
    const candidates = this.flattenResponseCandidates(response);
    const upstreamError = this.extractEmissionError(candidates);
    if (upstreamError) {
      throw new Error(upstreamError);
    }
    const result = { policyNumber: '', documentUrl: '', transactionId: '' };
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      result.policyNumber ||= this.toStringValue(record['policy_number'] || record['policyNumber'] || record['numPoliza']);
      result.documentUrl ||= this.toStringValue(record['download_url'] || record['document_url'] || record['url']);
      result.transactionId ||= this.toStringValue(
        record['transaction_id'] || record['transactionId'] || record['transaction_number'] || record['transactionNumber']
      );
    }
    if (!result.transactionId) {
      result.transactionId = result.policyNumber;
    }
    return result;
  }

  async emitSale(wizardFormValue: any): Promise<unknown> {
    if (this._isAssistanceProduct) {
      const dataToSend = await this.buildAssistanceDataToSend(wizardFormValue);
      return firstValueFrom(this.createAssistanceSale(dataToSend, wizardFormValue));
    }

    const payload = await this.buildInsuranceSalePayload(wizardFormValue);
    return firstValueFrom(this.createInsuranceSale(payload, wizardFormValue));
  }

  // Modo Telemarketing: arma el MISMO payload que emitSale, pero en vez de emitir
  // deja la venta PENDIENTE en core vía el BFF, que declara el sale_type. Adjunta
  // también el payload del ALTA (create_policy) armado ahora en la captura —
  // SIN numPoliza, que el BFF completará con el nº emitido al dar el alta.
  async createTmkPending(wizardFormValue: any): Promise<any> {
    const createPolicyPayload = this.buildCreatePolicyPayload(wizardFormValue);

    if (this._isAssistanceProduct) {
      const dataToSend = await this.buildAssistanceDataToSend(wizardFormValue);
      const body: any = this.buildEmissionBody(dataToSend, wizardFormValue);
      body.create_policy_payload = createPolicyPayload;
      return firstValueFrom(
        this.api.agricola.createTmkAssistancePending(body).observable<any>()
      );
    }

    const payload = await this.buildInsuranceSalePayload(wizardFormValue);
    const body: any = this.buildEmissionBody(payload, wizardFormValue);
    body.create_policy_payload = createPolicyPayload;
    return firstValueFrom(
      this.api.agricola.createTmkInsurancePending(body).observable<any>()
    );
  }

  // Records the create-policy payload (/ext/agricola/policies) instead of charging
  // the premium upstream: the backend persists it as a WebServiceLog for later
  // replay rather than calling SIS. Called right after emitSale, using the
  // partner-generated policy number (numPoliza) returned by the sale.
  async emitPolicy(wizardFormValue: any, numPoliza: string): Promise<unknown> {
    const payload = this.buildCreatePolicyPayload(wizardFormValue, numPoliza);
    return firstValueFrom(this.issuePolicy(payload));
  }

  // Wraps the upstream sale payload together with the full captured wizard
  // form, so the backend can audit-log everything collected (PEP, health
  // declaratoria, etc.) without forwarding it to the SIS core.
  private buildEmissionBody(payload: unknown, collectedData: unknown): { sale: unknown; collected_data: unknown } {
    return { sale: payload, collected_data: collectedData };
  }

  issuePolicy(payload: AgricolaCreatePolicyPayload): Observable<unknown> {
    return this.api.agricola.issuePolicy(payload)
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  // ¿Existe realmente el PDF de la póliza? Usa el mismo GET que abre la pestaña,
  // así que un 404 o una generación fallida se detecta acá y la pantalla de éxito
  // puede esconder el botón en vez de ofrecer un documento que no se puede abrir.
  // Nunca lanza: sin documento simplemente no hay botón.
  async hasPolicyDocument(policyNumber: string): Promise<boolean> {
    const number = this.toStringValue(policyNumber).trim();
    if (!number) {
      return false;
    }

    try {
      const response = await this.api.agricola.downloadPolicyDocument(number)
        .promise<HttpResponse<Blob>>();
      return Boolean(response.body?.size);
    } catch {
      return false;
    }
  }

  // Opens the emitted policy/certificate PDF in a new browser tab. GET-validates
  // first (no token in the URL — the BFF does not require one and reaches the core
  // via a Bearer service token) so a failed generation rejects here instead of
  // opening a blank tab.
  async downloadPolicy(policyNumber: string): Promise<void> {
    await this.api.agricola.downloadPolicyDocument(policyNumber)
      .promise<HttpResponse<Blob>>();
    window.open(this.api.agricola.policyDocumentUrl(policyNumber), '_blank');
  }

  // Saves the emitted policy/certificate PDF (document_without_signature_auth) as a
  // file. Used by the sell report's "Descargar certificado" when there is no signed
  // document attachment to fall back on.
  async downloadPolicyDocumentFile(policyNumber: string): Promise<void> {
    const response = await this.api.agricola.downloadPolicyDocument(policyNumber)
      .promise<HttpResponse<Blob>>();
    const blob = response.body;
    if (!blob) {
      throw new Error('El documento no devolvió contenido.');
    }
    const filename = this.parseContentDispositionFilename(response.headers.get('Content-Disposition'))
      || `certificado-${policyNumber}.pdf`;
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  // TODO: Connect when Agricola email APIs are available.
  resendPolicy(_policyNumber: string): Observable<null> {
    return of(null);
  }

  // -------------------------------------------------------------------------
  // Assistance contingency (forwarded to upstream contingency-sign).
  // The contingency PDF is persisted upstream; agricola only relays the multipart.
  // -------------------------------------------------------------------------

  // Builds the assistance sale, attaches the captured contingency PDF, and submits
  // it through the contingency proxy. Used instead of emitSale when a PDF is present.
  async emitAssistanceSaleContingency(wizardFormValue: any, pdf: Blob | File): Promise<unknown> {
    const dataToSend = await this.buildAssistanceDataToSend(wizardFormValue);
    const body = this.buildEmissionBody(dataToSend, wizardFormValue);
    return firstValueFrom(this.createAssistanceSaleContingency(pdf, body));
  }

  createAssistanceSaleContingency(pdf: Blob | File, body: unknown): Observable<unknown> {
    return this.api.agricola.createAssistanceSaleContingency(this.buildAssistanceContingencyFormData(pdf, body))
      .headers(this.emissionCorrelationHeaders())
      .observable<unknown>();
  }

  // Multipart body for contingency-sign: uploadFile (contingency PDF) + uploadData (sale JSON).
  buildAssistanceContingencyFormData(pdf: Blob | File, body: unknown): FormData {
    const formData = new FormData();
    formData.append('uploadFile', pdf, 'contingencia-asistencia.pdf');
    formData.append('uploadData', JSON.stringify(body));
    return formData;
  }

  private async cleanSalesPaymentInfo(
    paymentInfo: InsuranceSalePayload['payment_info'],
    holder: AgricolaInsuranceHolder
  ): Promise<InsuranceSalePayload['payment_info']> {
    // El banco puede devolver la cuenta enmascarada; se limpian los asteriscos
    // igual que en numCuenta, cuentaTDC y credit_number. Acá importa más porque
    // el número viaja cifrado: un valor sucio no falla, llega mal al descifrarlo.
    const paymentNumber = this.resolveEmissionPaymentNumber(paymentInfo);

    return {
      // El holder que entra ya viene con los teléfonos normalizados; el
      // responsable de pago propio se normaliza acá para que los dos viajen
      // igual dentro del mismo payload.
      payment_responsable: paymentInfo.payment_responsable
        ? this.withNormalizedPhones(paymentInfo.payment_responsable)
        : holder,
      premium: paymentInfo.premium,
      policy_charge: Number(paymentInfo.policy_charge) || 0,
      account_type: this.toStringValue(paymentInfo.account_type),
      payment_type: this.resolveAgricolaPaymentType(paymentInfo.account_type),
      // Viaja normalizado a '1'/'0' con el mismo helper que resuelve el
      // multiCobro de /policies, para que el archivo de altas y lo que Chubb
      // tiene registrado no puedan contradecirse. El proceso masivo lo lee de
      // payment_info.acceptsMulticharge; sin esta línea nunca le llega y el
      // multicobro sale siempre en "N".
      acceptsMulticharge: this.acceptsMultichargeValue(paymentInfo.acceptsMulticharge),
      payment_name: this.toStringValue(paymentInfo.payment_name || paymentInfo.account_name || holder.name),
      payment_number: await this.encryptPaymentNumber(paymentNumber),
      expiration_date: this.toStringValue(paymentInfo.expiration_date),
      card_type: this.toStringValue(paymentInfo.card_type),
      frequency: this.toStringValue(paymentInfo.frequency || 'E')
    };
  }

  // El número que viaja como payment_info.payment_number y del que el core S2
  // deriva TODO lo que Chubb registra del medio de cobro: billAccNum, el emisor
  // (ccCd/branchCd, por el primer dígito) y la referencia last_four_digits.
  //
  //   - TDC: 4111 + relleno + los últimos 4 de numCta, que en las filas de
  //     tarjeta llega como el PAN enmascarado ("************2095"). Es el mismo
  //     dato que ve el asesor en pantalla y el que ya alimenta cuentaTDC de
  //     /policies y el credit_number de asistencias, así que los tres no pueden
  //     contradecirse. Sin 4 dígitos la venta se detiene acá: emitir a medias
  //     dejaría la póliza registrada en Chubb contra un medio de pago que no es
  //     el que se va a cobrar.
  //   - Débito: el numCta tal cual. Es el número real de la cuenta y es el que
  //     el alta necesita; el relleno de tarjeta lo invalidaría.
  //
  // Ojo con codCtaTDC: la fila de la cuenta también trae ese código y el paso de
  // pago lo expone como cardLast4, pero no siempre coincide con el PAN (visto en
  // producción: numCta ***2095 contra codCtaTDC ***9013). Se usa sólo para
  // detectar que la fila es una TDC, nunca como origen de los últimos 4.
  private resolveEmissionPaymentNumber(paymentInfo: InsuranceSalePayload['payment_info']): string {
    const accountNumber = this.toStringValue(paymentInfo.payment_number || paymentInfo.account_number)
      .replace(/\*/g, '');

    if (!this.isCreditCardAccount(paymentInfo.account_type)) {
      return accountNumber;
    }

    const last4 = accountNumber.replace(/\D/g, '').slice(-4);
    if (last4.length < 4) {
      throw new Error('Los últimos 4 dígitos de la tarjeta no están precargados o son inválidos.');
    }

    return `${AGRICOLA_CARD_EMISSION_PREFIX}${last4}`;
  }

  private async encryptPaymentNumber(paymentNumber: string): Promise<string> {
    if (!paymentNumber) {
      return '';
    }

    if (!this.paymentCrypto) {
      throw new Error('Payment encryption service is not available.');
    }

    return this.paymentCrypto.encrypt(paymentNumber);
  }

  private normalizePreloadedPaymentInfo(client: AgricolaClientInfo | null): AgricolaPreloadedPaymentInfo | null {
    if (!client) {
      return null;
    }

    const clientPayload = this.clientInfoPayload(client);
    const rawCards = this.toStringValue(
      this.valueFromRecord(clientPayload, 'credit_card') ||
      this.valueFromRecord(clientPayload, 'card_last4') ||
      this.valueFromRecord(clientPayload, 'payment_number')
    );
    const cardOptions = this.parseCardOptions(rawCards);

    if (!cardOptions.length) {
      return null;
    }

    return {
      cardLast4: cardOptions[0],
      cardOptions,
      paymentName: this.toStringValue(this.valueFromRecord(clientPayload, 'name') || this.valueFromRecord(clientPayload, 'payment_name')),
      expirationDate: this.normalizeExpirationDate(this.valueFromRecord(clientPayload, 'expiration_date')),
      cardType: this.toStringValue(this.valueFromRecord(clientPayload, 'card_type')),
      accountType: this.toStringValue(this.valueFromRecord(clientPayload, 'account_type'))
    };
  }

  private buildHolderFromClientInfo(client: AgricolaClientInfo): AgricolaInsuranceHolder {
    const clientPayload = this.clientInfoPayload(client);
    const currentHolder = this.currentInsuranceInfo.holder || { identification_id: '', name: '' };
    const holder: AgricolaInsuranceHolder = {
      ...currentHolder
    };

    // Identity document. Upstream sends it as `numDocumento` (typed by
    // `tipoDocuIdentidad`); every document alias has to be exhausted before the
    // "número único" aliases, which are a different datum with their own path
    // (resolveClientUniqueNumber). Having them ahead of the documents is what
    // made the policy travel the número único as the holder's DUI; they stay
    // last so the field is never left empty when no document comes back.
    this.assignHolderValue(holder, 'identification_id', this.firstClientValue(clientPayload, [
      'identification_id',
      'numDocumento',
      'documentNumber',
      'document_number',
      'dui',
      'nit',
      'numUnico',
      'idUnico',
      'numeroUnico',
      'customerNumber'
    ]));
    this.assignHolderValue(holder, 'identification_type', this.firstClientValue(clientPayload, [
      'identification_type',
      'documentType',
      'document_type',
      'tipoDocumento',
      'tipoDocuIdentidad'
    ]));
    this.assignHolderValue(holder, 'name', this.firstClientValue(clientPayload, [
      'name',
      'fullName',
      'full_name',
      'nombreCompleto',
      'clientName',
      'customerName'
    ]));
    this.assignHolderValue(holder, 'first_name', this.firstClientValue(clientPayload, ['first_name', 'firstName', 'primerNombre', 'pnombre']));
    this.assignHolderValue(holder, 'middle_name', this.firstClientValue(clientPayload, ['middle_name', 'middleName', 'segundoNombre', 'snombre']));
    this.assignHolderValue(holder, 'last_name', this.firstClientValue(clientPayload, ['last_name', 'firstLastName', 'primerApellido', 'papellido']));
    this.assignHolderValue(holder, 'last_name_2', this.firstClientValue(clientPayload, ['last_name_2', 'secondLastName', 'segundoApellido', 'sapellido']));
    this.assignHolderValue(holder, 'married_last_name', this.firstClientValue(clientPayload, ['married_last_name', 'marriedLastName', 'apellidoCasada', 'acasada']));
    this.assignHolderValue(holder, 'nit', this.firstClientValue(clientPayload, ['nit']));
    this.assignHolderValue(holder, 'birthday', this.toIsoDate(this.firstClientValue(clientPayload, ['birthday', 'birthDate', 'birth_date', 'fechaNacimiento', 'fechaNac'])));
    this.assignHolderValue(holder, 'age', this.firstClientValue(clientPayload, ['age', 'edad']));
    this.assignHolderValue(holder, 'gender', this.firstClientValue(clientPayload, ['gender', 'genero', 'sexo']));
    this.assignHolderValue(holder, 'civil_status', this.firstClientValue(clientPayload, ['civil_status', 'civilStatus', 'estadoCivil']));
    this.assignHolderValue(holder, 'occupation', this.firstClientValue(clientPayload, ['occupation', 'ocupacion', 'actividadEconomica']));
    this.assignHolderValue(holder, 'nationality', this.firstClientValue(clientPayload, ['nationality', 'nacionalidad']));
    this.assignHolderValue(holder, 'country_id', this.firstClientValue(clientPayload, ['country_id', 'paisResidencia']));
    this.assignHolderValue(holder, 'country', this.firstClientValue(clientPayload, ['country', 'paisResidencia']));
    this.assignHolderValue(holder, 'email', this.firstClientValue(clientPayload, ['email', 'correo', 'correoElec']));
    this.assignHolderValue(holder, 'phone1', this.normalizePhoneNumber(this.firstClientValue(clientPayload, ['phone1', 'phone', 'telefono', 'telResidencia'])));
    this.assignHolderValue(holder, 'phone2', this.normalizePhoneNumber(this.firstClientValue(clientPayload, ['phone2', 'telefono2'])));
    this.assignHolderValue(holder, 'mobile', this.normalizePhoneNumber(this.firstClientValue(clientPayload, ['mobile', 'celular'])));
    this.assignHolderValue(holder, 'address', this.firstClientValue(clientPayload, ['address', 'direccion', 'dirAsegurado']));
    this.assignHolderValue(holder, 'account_type', this.firstClientValue(clientPayload, ['account_type', 'accountType']));
    // `code` is the client's "número único" — it travels downstream as
    // holder/insured/payment_responsable.code and is what the masivos alta reads
    // as numero_unico. SIS /clients/info returns it in body.numeroUnico (e.g.
    // "003567890"); the número único aliases MUST come first. `id`/`idOrigen`
    // stay only as a last-resort fallback: `id` is the WS envelope transaction
    // id (serie 1600xxx, e.g. "1600027") and `idOrigen` the branch code — never
    // the número único — so leaving them ahead made every policy carry that
    // fixed envelope id instead of the real client number.
    this.assignHolderValue(holder, 'code', this.firstClientValue(clientPayload, [
      'numeroUnico',
      'numUnico',
      'idUnico',
      'num_unico',
      'id_unico',
      'customerNumber',
      'code',
      'id',
      'idOrigen'
    ]));

    if (!holder.name) {
      holder.name = this.buildPersonName({
        firstName: holder.first_name,
        middleName: holder.middle_name,
        firstLastName: holder.last_name,
        secondLastName: holder.last_name_2,
        marriedLastName: holder.married_last_name
      });
    }

    return holder;
  }

  private clientInfoPayload(client: AgricolaClientInfo): Record<string, unknown> {
    const data = this.valueFromRecord(client, 'data');
    const nestedData = this.valueFromRecord(data, 'data');
    const body = [
      this.valueFromRecord(client, 'body'),
      this.valueFromRecord(data, 'body'),
      this.valueFromRecord(nestedData, 'body')
    ].find(value => this.isRecord(value));

    if (this.isRecord(body)) {
      const envelope = this.isRecord(nestedData)
        ? nestedData
        : this.isRecord(data)
          ? data
          : client;
      return { ...envelope, ...body };
    }

    if (this.isRecord(nestedData)) {
      return nestedData;
    }

    if (this.isRecord(data)) {
      return data;
    }

    return client;
  }

  private assignHolderValue(holder: AgricolaInsuranceHolder, key: keyof AgricolaInsuranceHolder, value: unknown): void {
    const normalizedValue = this.toStringValue(value).trim();
    if (normalizedValue) {
      holder[key] = normalizedValue;
    }
  }

  private firstClientValue(client: AgricolaClientInfo, keys: string[]): unknown {
    return keys
      .map(key => this.valueFromRecord(client, key))
      .find(value => this.toStringValue(value).trim());
  }

  private parseCardOptions(rawCards: unknown): string[] {
    return this.toStringValue(rawCards)
      .split(',')
      .map(card => card.trim())
      .filter(card => card.length > 0);
  }

  private normalizeExpirationDate(value: unknown): string {
    const raw = this.toStringValue(value).trim();

    if (!raw) {
      return '';
    }

    if (raw.includes('/')) {
      return raw.slice(0, 5);
    }

    const digits = raw.replace(/\D/g, '');
    if (digits.length >= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    }

    return raw;
  }

  private normalizeRelationshipOptions(response: unknown, strategy: RelationshipCatalogStrategy): RelationshipOption[] {
    const rows = this.extractCatalogRows(response);

    return rows
      .map((row): RelationshipOption | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        const rawValue = strategy === 'product-specific' ? row['id'] : row['code'];
        const rawLabel = strategy === 'product-specific' ? row['name'] : row['description'];
        const value = this.toStringValue(rawValue);
        const label = this.toStringValue(rawLabel);

        return value && label ? { value, label } : null;
      })
      .filter((option): option is RelationshipOption => option !== null);
  }

  private normalizeAssistanceValidation(response: unknown): AgricolaAssistanceValidation {
    const data = this.valueFromRecord(response, 'data');
    const nestedData = this.valueFromRecord(data, 'data');
    const source = this.isRecord(nestedData)
      ? nestedData
      : this.isRecord(data)
        ? data
        : this.isRecord(response)
          ? response
          : {};

    return {
      internal_product_code: this.toStringValue(source['internal_product_code']),
      is_assistance: source['is_assistance'] === true
    };
  }

  private normalizeAddOns(response: unknown): AgricolaAddOn[] {
    const rows = this.extractCatalogRows(response);

    return rows
      .map((row): AgricolaAddOn | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        const plan = Number(row['plan']);
        const assistancePlanId = Number(row['assistance_plan_id']);
        const planName = this.toStringValue(row['plan_name']);

        if (!Number.isFinite(plan) || !Number.isFinite(assistancePlanId)) {
          return null;
        }

        const addOn: AgricolaAddOn = {
          ...row,
          plan,
          plan_name: planName,
          price: Number(row['price']) || 0,
          assistance_plan_id: assistancePlanId
        };

        const description = this.toStringValue(row['description']);
        const currency = this.toStringValue(row['currency']);
        const config = this.parseConfig(row['config']);

        if (description) {
          addOn.description = description;
        }

        if (currency) {
          addOn.currency = currency;
        }

        if (Array.isArray(row['coverages'])) {
          addOn.coverages = row['coverages']
            .filter(coverage => this.isRecord(coverage))
            .map(coverage => ({
              ...coverage,
              detail: this.toStringValue(coverage['detail'] || coverage['coverage']),
              info: this.toStringValue(coverage['info'] || coverage['amount_insured'])
            }))
            .filter(coverage => coverage.detail || coverage.info);
        }

        if (config) {
          addOn.config = config;
        }

        return addOn;
      })
      .filter((addOn): addOn is AgricolaAddOn => addOn !== null);
  }

  private extractCatalogRows(response: unknown): unknown[] {
    let current = response;

    for (let i = 0; i < 2; i++) {
      if (Array.isArray(current)) {
        return current;
      }

      if (!this.isRecord(current) || !('data' in current)) {
        break;
      }

      current = current['data'];
    }

    return Array.isArray(current) ? current : [];
  }

  private filterValidBeneficiaries(beneficiaries: InsuranceBeneficiary[]): InsuranceBeneficiary[] {
    return (beneficiaries || []).filter(beneficiary =>
      !!beneficiary &&
      !!(this.toStringValue(beneficiary.name).trim() || this.toStringValue(beneficiary.identification_id).trim())
    );
  }

  private filterValidDependents(dependents: InsuranceDependent[]): InsuranceDependent[] {
    return (dependents || []).filter(dependent =>
      !!dependent &&
      !!(
        this.toStringValue(dependent.identification_id).trim() ||
        this.toStringValue(dependent.first_name).trim() ||
        this.toStringValue(dependent.name).trim()
      )
    );
  }

  private toInsuranceBeneficiaries(beneficiaries: AgricolaBeneficiary[] | InsuranceBeneficiary[]): InsuranceBeneficiary[] {
    return (beneficiaries || []).map(beneficiary => {
      const mapped: InsuranceBeneficiary = {
        name: this.toStringValue(beneficiary.name).trim(),
        relationship: this.toStringValue(beneficiary.relationship),
        percentage: Number(beneficiary.percentage) || 0,
        identification_id: this.toStringValue(beneficiary.identification_id).trim(),
        identification_type: beneficiary.identification_type == null ? null : Number(beneficiary.identification_type)
      };

      if (beneficiary.birthday) {
        mapped.birthday = beneficiary.birthday;
      }

      return mapped;
    });
  }

  private buildInsuranceHolder(
    mainData: Record<string, unknown>,
    complementary: Record<string, unknown>
  ): AgricolaInsuranceHolder {
    const currentHolder: Partial<AgricolaInsuranceHolder> = this.currentInsuranceInfo.holder || {};
    const identificationId = this.formValueOrCurrent(mainData['documentNumber'] || mainData['nit'], currentHolder.identification_id).trim();
    const countryId = this.formValueOrCurrent(complementary['country'], currentHolder.country_id);
    const isElSalvador = countryId === '4';
    const stateId = isElSalvador ? this.formValueOrCurrent(complementary['department'], currentHolder.state_id) : null;
    const substateId = isElSalvador ? this.formValueOrCurrent(complementary['municipality'], currentHolder.substate_id) : null;
    const cityId = isElSalvador ? this.formValueOrCurrent(complementary['district'], currentHolder.city_id) : null;
    const holderName = this.buildPersonName(mainData) || this.toStringValue(currentHolder.name);
    const holder: AgricolaInsuranceHolder = {
      ...currentHolder,
      identification_id: identificationId,
      identification_type: this.formValueOrCurrent(mainData['documentType'], currentHolder.identification_type),
      code: this.formValueOrCurrent(mainData['code'], currentHolder.code),
      account_type: this.formValueOrCurrent(mainData['accountType'], currentHolder['account_type']),
      name: holderName,
      first_name: this.formValueOrCurrent(mainData['firstName'], currentHolder.first_name),
      middle_name: this.formValueOrCurrent(mainData['middleName'], currentHolder.middle_name),
      last_name: this.formValueOrCurrent(mainData['firstLastName'], currentHolder.last_name),
      last_name_2: this.formValueOrCurrent(mainData['secondLastName'], currentHolder.last_name_2),
      married_last_name: this.formValueOrCurrent(mainData['marriedLastName'], currentHolder.married_last_name),
      nit: this.formValueOrCurrent(mainData['nit'], currentHolder.nit),
      civil_status: this.formValueOrCurrent(mainData['civilStatus'], currentHolder.civil_status),
      birthday: this.toIsoDate(this.formValueOrCurrent(mainData['birthDate'], currentHolder.birthday)),
      age: this.formValueOrCurrent(mainData['age'], currentHolder.age),
      gender: this.formValueOrCurrent(mainData['gender'], currentHolder.gender),
      occupation: this.formValueOrCurrent(complementary['occupation'], currentHolder.occupation),
      salary_range: this.formValueOrCurrent(complementary['salaryRange'], currentHolder.salary_range),
      country_id: countryId || null,
      country: this.resolveCountryName(countryId) || this.toStringValue(currentHolder.country),
      nationality: this.formValueOrCurrent(complementary['nationality'], currentHolder.nationality),
      address: this.formValueOrCurrent(complementary['address'], currentHolder.address),
      state_id: stateId || null,
      department: stateId ? this.resolveCatalogDescription(this.currentStates, stateId) || this.toStringValue(currentHolder.department) : '',
      substate_id: substateId || null,
      municipality: substateId ? this.resolveCatalogDescription(this.currentSubstates, substateId) || this.toStringValue(currentHolder.municipality) : '',
      city_id: cityId || null,
      district: cityId ? this.resolveCatalogDescription(this.currentCities, cityId) || this.toStringValue(currentHolder.district) : '',
      email: this.formValueOrCurrent(complementary['email'], currentHolder.email),
      phone1: this.normalizePhoneNumber(this.formValueOrCurrent(complementary['phone'], currentHolder.phone1)),
      phone2: this.normalizePhoneNumber(this.formValueOrCurrent(complementary['phone2'], currentHolder.phone2)),
      mobile: this.normalizePhoneNumber(this.formValueOrCurrent(complementary['mobile'], currentHolder.mobile))
    };

    if (!holder.name) {
      holder.name = this.currentInsuranceInfo.holder?.name || '';
    }

    if (!holder.identification_id) {
      holder.identification_id = this.currentInsuranceInfo.holder?.identification_id || '';
    }

    return holder;
  }

  private formValueOrCurrent(formValue: unknown, currentValue: unknown): string {
    const normalizedFormValue = this.toStringValue(formValue).trim();
    return normalizedFormValue || this.toStringValue(currentValue).trim();
  }

  private buildPaymentInfo(
    paymentInfo: Record<string, unknown>,
    premiumData: Record<string, unknown>,
    holder: AgricolaInsuranceHolder
  ): AgricolaInsurancePayment {
    const accountType = this.toStringValue(paymentInfo['accountType'] || holder['account_type']);
    const accountName = this.toStringValue(paymentInfo['accountName'] || holder.name);

    return {
      ...(this.currentInsuranceInfo.payment_info || { policy_charge: 0 }),
      payment_responsable: holder,
      premium: this.parsePremium(premiumData['premiumResult']),
      // Único punto donde se fija policy_charge para Agrícola, y depende del
      // tipo de cuenta:
      //   - Débito (corriente/ahorro): 1. El alta va por API, el banco debita la
      //     cuenta y la póliza nace pagada, así que Chubb la factura con el
      //     bill 2051 (cobro inmediato).
      //   - TDC: 0. No hay alta; la póliza nace impaga con el bill 2050 (no
      //     cobrar) y es el endoso el que la pasa a 2051 cuando el cobro entra.
      //     Cobrarla acá duplicaría el cargo contra ese 2050.
      // cleanSalesPaymentInfo propaga el valor tal cual
      // (Number(paymentInfo.policy_charge) || 0), así que este es el que llega
      // al payload de venta en ambos flujos.
      policy_charge: this.isCreditCardAccount(accountType) ? 0 : 1,
      account_type: accountType,
      account_number: this.toStringValue(paymentInfo['accountNumber']),
      account_name: accountName,
      account_status: this.toStringValue(paymentInfo['accountStatus']),
      auto_debit_authorized: !!paymentInfo['autoDebitAuthorized'],
      acceptsMulticharge: paymentInfo['acceptsMulticharge'] as string | number | boolean,
      payment_type: this.resolveAgricolaPaymentType(accountType),
      payment_name: this.normalizePaymentName(accountName),
      payment_number: this.toStringValue(paymentInfo['accountNumber']),
      frequency: 'E'
    };
  }

  // Public accessor used by the wizard to key its dynamic step configuration
  // off the selected product's internal code.
  getInternalProductCode(value: unknown): string {
    return this.resolveProductCode(value);
  }

  private resolveProductCode(value: unknown): string {
    const key = this.toStringValue(value).trim();
    const selectedProduct = this.currentProducts.find(product => String(product.id) === key);

    if (selectedProduct?.internal_product_code) {
      return selectedProduct.internal_product_code;
    }

    return PRODUCT_CODE_BY_SELECTION[key] || key || 'AGRICOLA_FRE_CYBER';
  }

  private parseConfig(value: unknown): Record<string, unknown> | null {
    if (this.isRecord(value)) {
      return { ...value };
    }

    const raw = this.toStringValue(value).trim();
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      return this.isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private normalizeProducts(response: unknown): AgricolaProduct[] {
    const rows = this.extractCatalogRows(response);

    return rows
      .map((row): AgricolaProduct | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        const id = Number(row['id']);
        const productName = this.toStringValue(row['product_name']);
        const internalProductCode = this.toStringValue(row['internal_product_code']);

        if (!Number.isFinite(id) || !productName || !internalProductCode) {
          return null;
        }

        const product: AgricolaProduct = {
          ...row,
          id,
          product_name: productName,
          internal_product_code: internalProductCode,
          is_assistance: row['is_assistance'] === true,
          has_condicionado: row['has_condicionado'] === true
        };

        const description = this.toStringValue(row['description']);
        const currency = this.toStringValue(row['currency']);

        if (description) {
          product.description = description;
        }

        if (currency) {
          product.currency = currency;
        }

        const config = this.parseConfig(row['config']);
        if (config) {
          product.config = config;
        }

        return product;
      })
      .filter((product): product is AgricolaProduct => product !== null);
  }

  // Las edades del holder (holder_minimum_age/holder_maximum_age) viven en el
  // config del PRODUCTO, no en el de cada plan. El endpoint las trae en
  // response.data.product.config, un nivel aparte del array de planes que
  // extractCatalogRows devuelve. Lo extraemos para usarlo como fallback del
  // rango de edad cuando la fila del plan no trae price_detail ni edades.
  private extractProductConfig(response: unknown): Record<string, unknown> | null {
    let current = response;

    for (let i = 0; i < 2; i++) {
      if (!this.isRecord(current)) {
        return null;
      }

      if (this.isRecord(current['product'])) {
        return this.parseConfig((current['product'] as Record<string, unknown>)['config']);
      }

      if (!('data' in current)) {
        return null;
      }

      current = current['data'];
    }

    return null;
  }

  private normalizePlans(response: unknown): AgricolaPlan[] {
    const rows = this.extractCatalogRows(response);
    const productLevelConfig = this.extractProductConfig(response);

    return rows
      .map((row): AgricolaPlan | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        const id = Number(row['id'] ?? row['plan']);
        const planCode = this.toStringValue(row['plan']);
        const planName = this.toStringValue(row['plan_name']);
        const description = this.toStringValue(row['description'] || planName);

        if (!Number.isFinite(id) || !planCode || !description) {
          return null;
        }

        const price = Number(row['price']) || 0;
        const assistancePlanId = Number(row['assistance_plan_id']);
        const planConfig = this.parseConfig(row['config']);
        const holderAgeBounds = this.extractHolderAgeBounds(planConfig)
          ?? this.extractHolderAgeBounds(productLevelConfig);
        const plan: AgricolaPlan = {
          ...row,
          id,
          plan: planCode,
          plan_name: planName || undefined,
          description,
          price,
          product: this.toStringValue(row['product']),
          insurance_plan_id: row['insurance_plan_id'] as string | number | undefined,
          price_detail: row['price_detail'],
          price_ranges: this.normalizePlanPriceRanges(row['price_detail'], price, holderAgeBounds)
        };

        const currencyCode = this.toStringValue(row['currency_code'] || row['currency']);
        if (currencyCode) {
          plan.currency_code = currencyCode;
        }

        if (Number.isFinite(assistancePlanId)) {
          plan.assistance_plan_id = assistancePlanId;
        }

        const coverages = this.normalizeCoverageRows(row['coverages']);
        if (coverages.length) {
          plan.coverages = coverages;
        }

        const config = planConfig;
        if (config) {
          plan.config = { ...config };
          if (Array.isArray(config['plan_detail'])) {
            plan.config.plan_detail = config['plan_detail']
              .filter(detail => this.isRecord(detail))
              .map((detail): AgricolaPlanDetail => ({
                ...detail,
                detail: this.toStringValue(detail['detail']),
                info: this.toStringValue(detail['info'])
              }))
              .filter(detail => detail.detail || detail.info);
          }
        }

        const productConfig = this.parseConfig(row['product_config']);
        if (productConfig) {
          plan.product_config = productConfig;
        }

        return plan;
      })
      .filter((plan): plan is AgricolaPlan => plan !== null);
  }

  private normalizeCoverageRows(value: unknown): AgricolaCoverage[] {
    const rows = Array.isArray(value)
      ? value
      : this.isRecord(value)
        ? Object.entries(value).map(([key, item]) => this.isRecord(item) ? { detail: key, ...item } : { detail: key, info: item })
        : [];

    return rows
            .filter(coverage => this.isRecord(coverage))
            .map(coverage => ({
              ...coverage,
              detail: this.toStringValue(coverage['detail'] || coverage['coverage']),
              info: this.toStringValue(coverage['info'] || coverage['amount_insured'])
            }))
            .filter(coverage => coverage.detail || coverage.info);
  }

  private normalizePlanPriceRanges(
    priceDetail: unknown,
    fallbackPrice: number,
    holderAgeBounds?: { from: number; to: number }
  ): AgricolaPlanPriceRange[] {
    const fallbackRange = this.buildFallbackPriceRange(fallbackPrice, holderAgeBounds);

    if (!Array.isArray(priceDetail) || !priceDetail.length) {
      return [fallbackRange];
    }

    const ranges = priceDetail
      .map((detail): AgricolaPlanPriceRange | null => {
        if (!this.isRecord(detail)) {
          return null;
        }

        const from = this.firstFiniteNumber([
          detail['from'],
          detail['age_from'],
          detail['min_age'],
          detail['min'],
          detail['start']
        ]);
        const to = this.firstFiniteNumber([
          detail['to'],
          detail['age_to'],
          detail['max_age'],
          detail['max'],
          detail['end']
        ]);
        const price = this.firstFiniteNumber([
          detail['price'],
          detail['amount'],
          detail['premium'],
          detail['value']
        ]);

        if (from == null || to == null || price == null) {
          return null;
        }

        return {
          label: `${from} - ${to} años`,
          value: `${from}-${to}`,
          from,
          to,
          price
        };
      })
      .filter((range): range is AgricolaPlanPriceRange => range !== null);

    return ranges.length ? ranges : [fallbackRange];
  }

  private buildFallbackPriceRange(
    fallbackPrice: number,
    holderAgeBounds?: { from: number; to: number }
  ): AgricolaPlanPriceRange {
    const from = holderAgeBounds?.from ?? 18;
    const to = holderAgeBounds?.to ?? 65;

    return {
      label: `${from} - ${to} años`,
      value: `${from}-${to}`,
      from,
      to,
      price: fallbackPrice
    };
  }

  private extractHolderAgeBounds(config: Record<string, unknown> | null): { from: number; to: number } | undefined {
    if (!config) {
      return undefined;
    }

    const from = this.firstFiniteNumber([config['holder_minimum_age']]);
    const to = this.firstFiniteNumber([config['holder_maximum_age']]);

    if (from == null || to == null || from > to) {
      return undefined;
    }

    return { from, to };
  }

  private normalizeCountries(response: unknown): AgricolaCountry[] {
    const rows = this.extractCatalogRows(response);

    return rows
      .map((row): AgricolaCountry | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        const id = Number(row['id']);
        const name = this.toStringValue(row['name']);

        if (!Number.isFinite(id) || !name) {
          return null;
        }

        const country: AgricolaCountry = {
          ...row,
          id,
          name
        };

        const code = this.toStringValue(row['code']);
        const kindCityId = Number(row['kind_city_id']);
        const kindCityName = this.toStringValue(row['kind_city_name']);
        const benlv = this.toStringValue(row['benlv']);

        if (code) {
          country.code = code;
        }

        if (Number.isFinite(kindCityId)) {
          country.kind_city_id = kindCityId;
        }

        if (kindCityName) {
          country.kind_city_name = kindCityName;
        }

        if (benlv) {
          country.benlv = benlv;
        }

        return country;
      })
      .filter((country): country is AgricolaCountry => country !== null);
  }

  private normalizeCatalogOptions(response: unknown): AgricolaCatalogOption[] {
    const rows = this.extractCatalogRows(response);

    return rows
      .map((row): AgricolaCatalogOption | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        
        const nestedIdentification = this.isRecord(row['identification']) ? row['identification'] : null;
        const source = nestedIdentification || row;
        const code = this.toStringValue(source['code'] || row['value'] || row['id']);
        const description = this.toStringValue(source['description'] || source['name'] || row['text']);

        if (!code || !description) {
          return null;
        }

        return {
          ...row,
          code,
          description
        };
      })
      .filter((option): option is AgricolaCatalogOption => option !== null);
  }

  private normalizeLocationOptions(response: unknown): AgricolaCatalogOption[] {
    const rows = this.extractCatalogRows(response);

    return rows
      .map((row): AgricolaCatalogOption | null => {
        if (!this.isRecord(row)) {
          return null;
        }

        const code = this.toStringValue(row['value'] || row['code'] || row['id']);
        const description = this.toStringValue(row['name'] || row['description'] || row['text']);

        if (!code || !description) {
          return null;
        }

        return {
          ...row,
          code,
          description
        };
      })
      .filter((option): option is AgricolaCatalogOption => option !== null);
  }

  private buildDestinations(complementary: Record<string, unknown>): AgricolaInsuranceDestination[] {
    const originId = this.toStringValue(complementary['country']);
    const destinationId = this.toStringValue(complementary['destination']);
    const origin = this.currentCountries.find(country => String(country.id) === originId);
    const destination = this.currentCountries.find(country => String(country.id) === destinationId);

    if (!origin || !destination) {
      return [];
    }

    let region = origin.kind_city_name === destination.kind_city_name
      ? 'DOMESTIC'
      : this.toStringValue(destination.kind_city_name);

    if (region === 'US') {
      region = 'REST OF THE WORLD';
    }

    return [{
      origin: origin.name,
      destination: destination.name,
      region
    }];
  }

  private resolveCountryName(value: unknown): string {
    const id = this.toStringValue(value);
    return this.currentCountries.find(country => String(country.id) === id)?.name || id;
  }

  private resolveCatalogDescription(options: AgricolaCatalogOption[], value: unknown): string {
    const code = this.toStringValue(value);
    return options.find(option => option.code === code)?.description || code;
  }

  private parsePremium(value: unknown): number | string {
    const rawValue = this.toStringValue(value).trim();
    const numericValue = Number(rawValue.replace(/[^0-9.]/g, ''));
    return Number.isFinite(numericValue) && rawValue ? numericValue : rawValue;
  }

  private normalizePaymentName(value: unknown): string {
    return this.toStringValue(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private toIsoDate(value: unknown): string {
    const raw = this.toStringValue(value).trim();
    if (!raw) {
      return '';
    }

    const isoParts = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoParts) {
      return [
        isoParts[1],
        isoParts[2].padStart(2, '0'),
        isoParts[3].padStart(2, '0')
      ].join('-');
    }

    const slashParts = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashParts) {
      return [
        slashParts[3],
        slashParts[2].padStart(2, '0'),
        slashParts[1].padStart(2, '0')
      ].join('-');
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${parsed.getFullYear()}-${month}-${day}`;
  }

  private buildPersonName(mainData: Record<string, unknown>): string {
    return [
      mainData['firstName'],
      mainData['middleName'],
      mainData['firstLastName'],
      mainData['secondLastName'],
      mainData['marriedLastName']
    ]
      .map(value => this.toStringValue(value).trim())
      .filter(value => value && value.toUpperCase() !== 'N/A')
      .join(' ');
  }

  private planIdFromValue(planOption: unknown): number {
    const rawValue = this.toStringValue(planOption);
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue) && rawValue) {
      return numericValue;
    }

    const selectedPlan = this.currentPlans.find(plan => plan.plan === rawValue || String(plan.id) === rawValue);
    if (selectedPlan) {
      return Number(selectedPlan.plan) || selectedPlan.id;
    }

    const planMap: Record<string, number> = {
      op1: 1,
      op2: 2,
      op3: 3
    };

    return planMap[rawValue] || 1;
  }

  getPlanByOption(planOption: string): AgricolaPlan | null {
    return this.currentPlans.find(plan => plan.plan === planOption || String(plan.id) === planOption) || null;
  }

  // Opt-in flag: an assistance offers the dependants step only when its plan or
  // product config declares `dependents: true`. Read from the same config sources
  // as the other assistance flags. Only the assistance flows consult it; the
  // insurance flows keep their static step list. `max_dependants` still caps how
  // many can be added once the step is on.
  allowsDependants(wizardFormValue: any): boolean {
    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const selectedProduct = this.resolveSelectedProduct(wizardFormValue);
    const candidates = [
      selectedPlan?.config?.[DEPENDENTS_CONFIG_KEY],
      selectedPlan?.product_config?.[DEPENDENTS_CONFIG_KEY],
      selectedProduct?.config?.[DEPENDENTS_CONFIG_KEY]
    ];

    return candidates.some(value => this.coerceBoolean(value) === true);
  }

  // Cap on dependants for the selected plan/product, or null when unbounded.
  // Resolution mirrors allowsDependants: plan config first, then product config,
  // so a limit set at either level is honored. The canonical key is
  // `max_dependents` (the core spelling, with an e); `max_dependants` (with an a)
  // is kept as a lower-priority fallback for any older config that used it, since
  // a missing key here silently removes the cap.
  resolveMaxDependants(wizardFormValue: any): number | null {
    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const selectedProduct = this.resolveSelectedProduct(wizardFormValue);
    const candidates = [
      // Canonical spelling first, at plan then product level.
      selectedPlan?.config?.['max_dependents'],
      selectedPlan?.product_config?.['max_dependents'],
      selectedProduct?.config?.['max_dependents'],
      // Legacy 'a' spelling as a fallback, same plan -> product order.
      selectedPlan?.config?.['max_dependants'],
      selectedPlan?.product_config?.['max_dependants'],
      selectedProduct?.config?.['max_dependants']
    ];

    for (const value of candidates) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      const num = Number(value);
      // A configured 0 is a real cap (no dependants allowed), so only skip
      // non-numeric values, never a valid 0.
      if (Number.isFinite(num) && num >= 0) {
        return num;
      }
    }

    return null;
  }

  private resolveSelectedPlan(wizardFormValue: any): AgricolaPlan | null {
    const planOption = this.toStringValue(wizardFormValue?.step3?.premiumData?.planOption || '');
    return this.getPlanByOption(planOption);
  }

  private resolveSelectedProduct(wizardFormValue: any): AgricolaProduct | null {
    const productSelection = wizardFormValue?.step1?.productSelection?.product;
    const selectedId = this.toStringValue(productSelection);
    const selectedCode = this.resolveProductCode(productSelection);

    return this.currentProducts.find(product => String(product.id) === selectedId || product.internal_product_code === selectedCode) || null;
  }

  private resolveAssistancePlanConfig(selectedPlan: AgricolaPlan | null): Record<string, unknown> | undefined {
    return selectedPlan?.config ? { ...selectedPlan.config } : undefined;
  }

  private resolveSingleClientAssistancePlan(selectedPlan: AgricolaPlan | null): boolean | null {
    const planValue = this.coerceBoolean(selectedPlan?.config?.['single_client_assistance_plan']);
    if (planValue !== null) {
      return planValue;
    }

    return this.coerceBoolean(selectedPlan?.product_config?.['single_client_assistance_plan']);
  }

  private resolveAssistancePlanId(selectedPlan: AgricolaPlan | null): number {
    const assistancePlanId = Number(selectedPlan?.assistance_plan_id);
    if (Number.isFinite(assistancePlanId)) {
      return assistancePlanId;
    }

    throw new Error('El plan seleccionado no incluye assistance_plan_id para emitir asistencia.');
  }

  private async buildAssistancePrivateLinkPayload(
    wizardFormValue: any,
    token: string,
    finalUrl: string,
    channel?: 'email' | 'sms'
  ): Promise<AgricolaAssistancePrivateLinkCreatePayload> {
    const dataToSend = await this.buildAssistanceDataToSend(wizardFormValue, {
      origin: 'externo',
      finalUrl,
      sentChannel: channel
    });
    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    return {
      key_: token,
      request: JSON.stringify({
        dataToSend,
        planInfo: selectedPlan ? { ...selectedPlan } : null
      }),
      status: 0,
      policy_number: ''
    };
  }

  // Contact resolution for the private-link send. Shared by the notification payload
  // (what we actually send to) and the sale's sent_to_* extraData, so both stay in sync.
  private resolvePrivateLinkEmail(wizardFormValue: any, holder: any): string {
    return this.toStringValue(wizardFormValue?.step1?.complementary?.email || holder?.email);
  }

  private resolvePrivateLinkPhone(wizardFormValue: any, holder: any): string {
    // Acá el número es el destino real del SMS, así que un candidato que sea
    // puro relleno de ceros no puede ganarle a uno que sí se puede marcar.
    return this.firstRealPhone(
      wizardFormValue?.step1?.complementary?.mobile,
      wizardFormValue?.step1?.complementary?.phone,
      holder?.mobile,
      holder?.phone1
    );
  }

  private buildAssistancePrivateLinkNotification(
    wizardFormValue: any,
    finalUrl: string,
    channel: 'email' | 'sms'
  ): AgricolaAssistancePrivateLinkNotificationPayload {
    const holder = (this.updateCurrentInsuranceFromWizard(wizardFormValue).holder || {
      identification_id: '',
      name: ''
    }) as AgricolaInsuranceHolder;
    const selectedPlan = this.resolveSelectedPlan(wizardFormValue);
    const product = this.resolveSelectedProduct(wizardFormValue);
    const planId = Number(selectedPlan?.id || selectedPlan?.plan || selectedPlan?.assistance_plan_id);
    if (!Number.isFinite(planId) || !planId) {
      throw new Error('El plan seleccionado no es válido para enviar el private link de asistencia.');
    }
    const phone = this.resolvePrivateLinkPhone(wizardFormValue, holder);
    const payload: AgricolaAssistancePrivateLinkNotificationPayload = {
      name: this.toStringValue(holder.name || this.buildPersonName({
        firstName: holder.first_name,
        middleName: holder.middle_name,
        firstLastName: holder.last_name,
        secondLastName: holder.last_name_2,
        marriedLastName: holder.married_last_name
      }) || 'Cliente'),
      phone,
      sponsor: 'generic',
      product_code: this.toStringValue(product?.internal_product_code || this.currentInsuranceInfo.product_code),
      plan_id: planId
    };

    if (channel === 'email') {
      payload.email = this.resolvePrivateLinkEmail(wizardFormValue, holder);
      payload.url = finalUrl;
      return payload;
    }

    payload.link = finalUrl;
    return payload;
  }

  private normalizeAssistancePrivateLinkState(
    response: unknown,
    token: string,
    finalUrl: string
  ): AgricolaAssistancePrivateLinkState {
    const candidates = this.flattenResponseCandidates(response);
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      const record = candidate as Record<string, unknown>;
      const emissionLog = this.isRecord(record['emission_log'])
        ? record['emission_log'] as Record<string, unknown>
        : null;
      const emissionLogId = Number(
        emissionLog?.['id']
        || record['emission_log_id']
        || record['id']
      );

      if (!Number.isFinite(emissionLogId)) {
        continue;
      }

      return {
        emissionLogId,
        token: this.toStringValue(emissionLog?.['key_'] || record['key_'] || token),
        status: Number(emissionLog?.['status'] || record['status']) || 0,
        policyNumber: this.toStringValue(emissionLog?.['policy_number'] || record['policy_number']),
        finalUrl
      };
    }

    throw new Error('No se pudo crear el private link de asistencia.');
  }

  private flattenResponseCandidates(response: unknown): unknown[] {
    const candidates: unknown[] = [response];
    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      candidates.push(record['data']);
      if (record['data'] && typeof record['data'] === 'object') {
        candidates.push((record['data'] as Record<string, unknown>)['data']);
      }
    }

    return candidates;
  }

  // Detects an error reported inside an otherwise HTTP-200 emission envelope
  // (the upstream returns `{ error, transactionNumber }` when the sale was
  // already placed). Returns the message so the caller can throw instead of
  // treating the response as a successful emission.
  private extractEmissionError(candidates: unknown[]): string {
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      const message = this.toStringValue(record['error'] || record['error_message'] || record['errorMessage']).trim();
      if (message) {
        return this.translateEmissionError(message);
      }
    }
    return '';
  }

  private translateEmissionError(message: string): string {
    if (/sale already placed/i.test(message)) {
      return 'La venta ya fue emitida previamente. No es necesario emitir de nuevo.';
    }
    return message;
  }

  private buildAssistancePrivateLinkUrl(token: string): string {
    const baseUrl = this.assistancePaymentPublicUrl();
    if (!baseUrl) {
      throw new Error('No existe assistancePaymentPublicUrl configurado.');
    }

    return `${baseUrl}/public/assistance-insurance-payment?id=${encodeURIComponent(token)}&origin=externo`;
  }

  private assistancePaymentPublicUrl(): string {
    return this.toStringValue(environment.assistancePaymentPublicUrl).replace(/\/+$/, '');
  }

  private generatePrivateLinkToken(): string {
    const cryptoRef = globalThis.crypto;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID().replace(/-/g, '');
    }

    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 18)}`.slice(0, 32).padEnd(32, '0');
  }

  private async genderToS2(value: unknown): Promise<number | null> {
    const rawValue = this.toStringValue(value).trim();
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue) && rawValue) {
      return numericValue;
    }

    if (!this.currentGenders.length) {
      await firstValueFrom(this.getGenders());
    }

    const normalizedValue = this.normalizeAscii(rawValue);
    const match = this.currentGenders.find(gender => {
      const code = this.toStringValue(gender.code).trim();
      const description = this.normalizeAscii(gender.description);
      return code === rawValue || description === normalizedValue || description.startsWith(normalizedValue);
    });
    const code = Number(match?.code);

    return Number.isFinite(code) ? code : null;
  }

  private acceptsMultichargeValue(value: unknown): string {
    if (value === true || value === 1 || value === '1') {
      return '1';
    }

    return '0';
  }

  private coerceBoolean(value: unknown): boolean | null {
    if (value === true || value === false) {
      return value;
    }

    const normalized = this.normalizeAscii(this.toStringValue(value));
    if (normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === '0' || normalized === 'no') {
      return false;
    }

    return null;
  }

  private resolveClientUniqueNumber(wizardFormValue: any): string {
    const currentClient = this._currentClient;
    const currentClientPayload = currentClient ? this.clientInfoPayload(currentClient) : null;
    const clientCandidates = [
      this.valueFromRecord(currentClientPayload, 'numUnico'),
      this.valueFromRecord(currentClientPayload, 'idUnico'),
      this.valueFromRecord(currentClientPayload, 'numeroUnico'),
      this.valueFromRecord(currentClientPayload, 'num_unico'),
      this.valueFromRecord(currentClientPayload, 'id_unico'),
      this.valueFromRecord(currentClientPayload, 'customerNumber')
    ];

    const mainData = wizardFormValue?.step1?.mainData || {};
    const formCandidates = [
      mainData.uniqueNumber,
      mainData.numeroUnico,
      mainData.clientNumber,
      mainData.documentNumber,
      mainData.nit
    ];

    const searchCandidate = this._lastClientSearch?.searchType === 'numero-unico'
      ? this._lastClientSearch.searchValue
      : '';

    return [
      ...clientCandidates,
      searchCandidate,
      ...formCandidates
    ]
      .map(value => this.toStringValue(value).trim())
      .find(value => !!value) || '';
  }

  private resolveSisProductCode(product: unknown, planOption: unknown): string {
    const productCode = SIS_PRODUCT_CODES[this.toStringValue(product)];
    const planCode = SIS_PRODUCT_CODES[this.toStringValue(planOption)];
    return productCode || planCode || SIS_AGRICOLA_DEFAULTS.defaultProductCode;
  }

  private resolveAllyCode(ally: unknown): string {
    return SIS_ALLY_CODES[this.toStringValue(ally).trim().toUpperCase()] || SIS_AGRICOLA_DEFAULTS.allyCode;
  }

  // Maps CodTipoCta (from /clients/accounts) to tipoCuenta for /balance/validate.
  // Consulta Cuenta: 1=Corriente, 2=Ahorro, 3=TDC
  // Validar Fondos:  1=Ahorro o Corriente, 2=TDC
  //
  // Un tipo vacío o irreconocible cae en TDC, no en cuenta. La regla de negocio
  // es que una TDC nunca se registra por API, así que el error caro es el falso
  // "1": trataría una tarjeta como cuenta y mandaría al banco un alta que no
  // corresponde, imposible de deshacer desde acá. El falso "2" sólo omite un
  // alta que después se puede reponer, y no rompe el flujo (a diferencia de
  // lanzar, que tumbaría /balance/validate en pleno wizard cada vez que el
  // tipo de cuenta todavía no está cargado).
  private balanceValidationTypeCode(accountType: unknown): string {
    const raw = this.toStringValue(accountType).trim();
    if (raw === '3') {
      return AGRICOLA_TDC_CHARGE_CODE;
    }
    if (raw === '1' || raw === '2') {
      return '1';
    }

    const value = raw
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    // Mismo orden de precedencia que accountTypeCodeFromValue en el paso de pago,
    // que es de donde vienen las etiquetas ("2 - CUENTA FACIL", "AHORROS", ...).
    const leadingCode = /^[123]\b/.exec(value)?.[0];
    if (leadingCode) {
      return leadingCode === '3' ? AGRICOLA_TDC_CHARGE_CODE : '1';
    }
    if (value.includes('tarjeta') || value.includes('tdc') || value.includes('credit')) {
      return AGRICOLA_TDC_CHARGE_CODE;
    }
    if (value.includes('ahorro') || value.includes('corriente') || value.includes('cuenta')) {
      return '1';
    }

    return AGRICOLA_TDC_CHARGE_CODE;
  }

  private formatSisMoney(value: unknown): string {
    const rawValue = this.toStringValue(value).replace(/[$,\s]/g, '');
    if (!rawValue) {
      return '';
    }

    const amount = Number(rawValue);

    if (!Number.isFinite(amount)) {
      return '';
    }

    return Math.round(amount * 100).toString();
  }

  private moneyAmount(value: unknown): number {
    const rawValue = this.toStringValue(value).replace(/[$,\s]/g, '');
    const amount = Number(rawValue);

    return Number.isFinite(amount) ? amount : 0;
  }

  private normalizeAscii(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private todayIsoDate(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private firstFiniteNumber(values: unknown[]): number | null {
    for (const value of values) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }
    }

    return null;
  }

  private ensureCurrentInsuranceInfo(): void {
    if (!this.currentInsuranceInfo.date_issued || !this.currentInsuranceInfo.date_effective) {
      const previous = { ...this.currentInsuranceInfo };
      this.initGenericInsurance();
      this.currentInsuranceInfo = {
        ...this.currentInsuranceInfo,
        ...previous,
        payment_info: {
          ...(this.currentInsuranceInfo.payment_info || { policy_charge: 0 }),
          ...(previous.payment_info || {})
        }
      };
    }
  }

  private toStringValue(value: unknown): string {
    return value == null ? '' : String(value);
  }

  // S2 devuelve los teléfonos rellenados con ceros a la izquierda hasta 11
  // dígitos: el celular '00078612428' y, cuando el cliente no tiene fijo,
  // telResidencia '00000000000'. El relleno es del campo de origen, no del
  // número, así que se quita apenas el dato entra al holder y desde ahí el
  // formulario y los payloads ya lo ven limpio.
  //
  // Un valor que era todo relleno se queda vacío a propósito: no es un teléfono,
  // y devolverlo vacío es lo que deja que quien lo consulta caiga en el
  // siguiente contacto (el celular) en vez de mandar once ceros.
  //
  // Sólo se tocan valores de puros dígitos. Uno con guiones o espacios se deja
  // tal cual en vez de arriesgar a recortarle un prefijo que sí signifique algo.
  //
  // Público porque el paso de pago decide con la misma regla si hay un número al
  // cual mandarle el enlace de firma: si el guard aceptara un relleno que acá se
  // descarta, el SMS saldría hacia un teléfono que no existe.
  normalizePhoneNumber(value: unknown): string {
    const raw = this.toStringValue(value).trim();
    if (!raw || !/^\d+$/.test(raw)) {
      return raw;
    }

    return raw.replace(/^0+/, '');
  }

  // Primer teléfono con dígitos reales de los candidatos, en orden de
  // preferencia.
  private firstRealPhone(...values: unknown[]): string {
    return values.map(value => this.normalizePhoneNumber(value)).find(phone => phone) || '';
  }

  // Copia del titular con los teléfonos ya normalizados, para las personas que
  // no pasan por los dos armadores del holder (el asegurado y el responsable de
  // pago propios traen su propio objeto).
  private withNormalizedPhones(person: AgricolaInsuranceHolder): AgricolaInsuranceHolder {
    const normalized: AgricolaInsuranceHolder = { ...person };

    for (const key of ['phone1', 'phone2', 'mobile'] as const) {
      if (normalized[key] !== undefined) {
        normalized[key] = this.normalizePhoneNumber(normalized[key]);
      }
    }

    return normalized;
  }

  getCatalogEquivalences(params?: any): Observable<any> {
    return this.api.agricola.getCatalogEquivalences(params).observable<any>();
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private valueFromRecord(record: unknown, key: string): unknown {
    return this.isRecord(record) ? record[key] : undefined;
  }
}
